import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { z } from 'zod';

import { PlateStateAbbreviation } from '@/constants.ts';
import {
  type ActiveSessionCheckResponse,
  ActiveSessionCheckResponseSchema,
} from '@/features/plate-lookup/api/schemas';
import {
  type CreatedSupportRequest,
  CreatedSupportRequestSchema,
  type SupportMessage,
  SupportMessageSchema,
  type SupportRequest,
  type SupportRequestDetail,
  SupportRequestDetailSchema,
  SupportRequestSchema,
} from '@/features/support/types';
import { getApiBaseUrl } from '@/legacy/data/apiHelpers';
import {
  RotationScheduleSchema,
  type RotationScheduleType,
  SubLocationsOfLocationListSchema,
} from '@/lib/api/schemas/sublocations';
import { DateTime } from '@/utils/dates';
import { removeEmpty, removeNullValuesFromObject } from '@/utils/primitives';
import { zodDuration } from '@/utils/zodUtils';

import {
  AccessPointCommandResult,
  type AccessPointCommandResultType,
  AccessPointList,
  type AccessPointListType,
  AssociatedCameraList,
  type AssociatedCameraListType,
} from './schemas/access-point';
import {
  type AddSeatVehicleRequestType,
  AgreementDetailSchema,
  type AgreementDetailType,
  AgreementInvoiceSchema,
  AgreementInvoicesPaginatedResult,
  type AgreementInvoicesPaginatedResultType,
  type AgreementInvoiceType,
  AgreementPaginatedResult,
  type AgreementPaginatedResultType,
  type AgreementPaginationKey,
  type ChangeCollectionMethodRequestType,
  ChangeCollectionMethodResponseSchema,
  type ChangeCollectionMethodResponseType,
  ChangeGroupPayerResponseSchema,
  type ChangeGroupPayerResponseType,
  type CreateGroupRequestSchemaType,
  type CreateLeaseAllocationRequestType,
  type ExchangeTierReservedSpaceRequestType,
  GroupAgreementDetailSchema,
  type GroupAgreementDetailType,
  GroupAgreementDocumentResponseSchema,
  GroupAgreementDocumentSchema,
  GroupAgreementDocumentsResponseSchema,
  type GroupAgreementDocumentType,
  GroupAgreementPaginatedResult,
  type GroupAgreementPaginatedResultType,
  type GroupAgreementPaginationKey,
  GroupAgreementSchema,
  type GroupAgreementType,
  GroupDocumentType,
  GroupDocumentUploadSchema,
  type GroupDocumentUploadType,
  GroupInvoiceSchema,
  GroupInvoicesPaginatedResult,
  type GroupInvoicesPaginatedResultType,
  type GroupInvoiceType,
  type GrowTierSeatsRequestType,
  type InvoicePaginationKey,
  LeaseAllocationListSchema,
  LeaseAllocationSchema,
  type LeaseAllocationType,
  type LeaseAllocationWithTiersType,
  type ReduceTierSeatsRequestType,
  type SeatPaginationKey,
  SeatResponseSchema,
  type SeatResponseType,
  SeatSchema,
  type SeatType,
  SeatVehicleSchema,
  type SeatVehicleType,
  SendInvoicePreviewResponseSchema,
  type SendInvoicePreviewResponseType,
  SubagreementSchema,
  type SubagreementType,
  SwapSeatsResponseSchema,
  type SwapSeatsResponseType,
  TierSeatsPaginatedResult,
  type TierSeatsPaginatedResultType,
  type UpdateLeaseAllocationRequestType,
  type UpdateSeatVehicleRequestType,
  type UpdateTierCapacityPolicyRequestType,
} from './schemas/agreement';
import {
  ANALYTICS_MAX_BATCH,
  type AnalyticsCatalog,
  AnalyticsCatalogSchema,
  type AnalyticsQueryBatchResponse,
  AnalyticsQueryBatchResponseSchema,
  type AnalyticsWidgetQuery,
} from './schemas/analytics';
import {
  BankAccount,
  BankAccountList,
  type BankAccountListType,
  BankAccountType,
  type UpdateBankAccountBrandingInput,
} from './schemas/bank-account';
import {
  type Board,
  type BoardContent,
  type BoardList,
  BoardListSchema,
  BoardSchema,
  type DefaultBoard,
  DefaultBoardSchema,
} from './schemas/boards';
import {
  type BroadcastCampaign,
  BroadcastCampaignSchema,
  type BroadcastCompose,
  BroadcastListResponseSchema,
  type BroadcastPreviewRequest,
  type BroadcastPreviewResponse,
  BroadcastPreviewResponseSchema,
} from './schemas/broadcasts';
import {
  CameraAlertListSchema,
  type CameraAlertListType,
  CameraAlertSchema,
  type CameraAlertType,
  CameraListSchema,
  type CameraListType,
  CameraMetricsSchema,
  type CameraMetricsType,
  CameraSchema,
  type CameraType,
  type CreateCameraRequestType,
  type CreateMaintenanceWindowRequestType,
  FleetHealthSchema,
  type FleetHealthType,
  MaintenanceWindowListSchema,
  type MaintenanceWindowListType,
  MaintenanceWindowSchema,
  type MaintenanceWindowType,
  type UpdateCameraRequestType,
  type UpdateMaintenanceWindowRequestType,
} from './schemas/camera';
import {
  CameraEventList,
  type CameraEventListType,
  CameraEventsPaginatedResult,
  type CameraEventType,
  ConfirmCameraEventRequest,
} from './schemas/camera-event';
import {
  CapacityCommitmentsSchema,
  type CapacityCommitmentsType,
  CapacityStatsSchema,
  type CapacityStatsType,
  type CreateReservedSpacesRequestType,
  ReservedSpaceListSchema,
  type ReservedSpaceListType,
  ReservedSpaceSchema,
  type ReservedSpaceType,
} from './schemas/capacity';
import {
  ChalkSchema,
  type ChalkType,
  type CreateChalkInput,
} from './schemas/chalk';
import {
  Customer,
  type CustomerCreateSchemaType,
  CustomerList,
  type CustomerListType,
  type CustomerType,
} from './schemas/customer';
import {
  DripFunnelMetricsSchema,
  type DripFunnelMetricsType,
  EnforcementMetricsSchema,
  type EnforcementMetricsType,
  MonthlyParkerMetricsSchema,
  type MonthlyParkerMetricsType,
  ParkingMetricsSchema,
  type ParkingMetricsType,
} from './schemas/dashboard';
import {
  AccessControlOverride,
  type AccessControlOverrideType,
  AccessControlPeriod,
  type AccessControlPeriodType,
  AccessControlSchedule,
  type AccessControlScheduleType,
  AccessDecisionsResponse,
  type AccessDecisionsResponseType,
  AccessPointSchedule,
  type AccessPointScheduleType,
  CameraCaptureResponse,
  type CameraCaptureResponseType,
  type CreateDeviceRequestType,
  type CreateOverrideRequestType,
  type CreatePeriodRequestType,
  type CreateScheduleRequestType,
  Device,
  DeviceCommandResult,
  type DeviceCommandResultType,
  type DeviceCommandType,
  DeviceList,
  type DeviceListType,
  type DeviceType,
  ScheduleTemplatesResponse,
  type ScheduleTemplatesResponseType,
  type SetAccessPointScheduleRequestType,
  type UpdateDeviceRequestType,
  type UpdateOverrideRequestType,
  WhitelistEntriesResponse,
  type WhitelistEntriesResponseType,
  WhitelistStatus,
  type WhitelistStatusType,
} from './schemas/device';
import {
  type EnforcementStatement,
  EnforcementStatementListSchema,
  EnforcementStatementSchema,
  type SaveEnforcementStatementsResult,
  SaveEnforcementStatementsResultSchema,
} from './schemas/enforcement-statement';
import {
  type AuditTrailPaginationKey,
  GroupAuditTrailPaginatedResult,
  type GroupAuditTrailPaginatedResultType,
} from './schemas/group-audit-trail';
import {
  GroupOverageCountedVehiclesSchema,
  type GroupOverageCountedVehiclesType,
  GroupOverageUsageSchema,
  type GroupOverageUsageType,
  type UpdateGroupOverageSettingsRequest,
} from './schemas/groupOverage';
import {
  type DiagnosesListParams,
  DiagnosisSchema,
  type DiagnosisType,
  HealthConfigSchema,
  type HealthConfigType,
  PaginatedDiagnosesSchema,
  type PaginatedDiagnosesType,
  type SaveHealthConfigPayloadType,
} from './schemas/health';
import {
  CreateLocationPayloadSchemaType,
  LocationKind,
  LocationList,
  LocationListType,
  LocationSchema,
  LocationType,
  UpdateLocationParams,
} from './schemas/location';
import {
  type CreateLocationAnnouncementPayload,
  type LocationAnnouncement,
  type LocationAnnouncementList,
  LocationAnnouncementListSchema,
  LocationAnnouncementSchema,
  type UpdateLocationAnnouncementPayload,
} from './schemas/location-announcement';
import {
  type LocationLayout,
  type LocationLayoutList,
  LocationLayoutListSchema,
  LocationLayoutSchema,
  type UpdateLocationLayoutPayload,
} from './schemas/location-layout';
import {
  type ClearSessionPhoneRequest,
  type ClearSessionPhoneResponse,
  ClearSessionPhoneResponseSchema,
  type CorrectPlateRequest,
  type CorrectPlateResponse,
  CorrectPlateResponseSchema,
  ParkingSessionsPaginatedResult,
  PrivateSessionResponseSchema,
} from './schemas/parking-session';
import {
  ParkpliantGroupListSchema,
  ParkpliantGroupType,
} from './schemas/parkpliantGroup';
import {
  CancelSubscriptionSchemaType,
  CreatePermitSchemaType,
  Permit,
  PermitCoverageResult,
  type PermitCoverageResultType,
  PermitList,
  type PermitPaginationKey,
  PermitsPaginatedResult,
  type PermitsPaginatedResultType,
  type QuickPermitFormSchemaType,
  type UpdatePermitSchemaType,
} from './schemas/permit';
import {
  PermitVisitsResultSchema,
  type PermitVisitStartKey,
} from './schemas/permit-visit';
import {
  AccessPointLockdownOverride,
  type AccessPointLockdownOverrideType,
  type CreateLockdownOverrideRequestType,
  type CreatePINRequestType,
  PIN,
  PINList,
  type PINListType,
  type PINType,
} from './schemas/pin';
import {
  type ClonePlanVersionSchemaType,
  type CreatePlanSchemaType,
  type CreatePlanVersionSchemaType,
  PlanListSchema,
  type PlanListType,
  PlanSchema,
  type PlanType,
  PlanVersionSchema,
  type PlanVersionType,
  RateChangeResponseSchema,
  type RateChangeResponseType,
  type RateChangeSchemaType,
  type UpdatePlanSchemaType,
} from './schemas/plan';
import {
  type CreateReportScheduleRequestType,
  ReportSchedule,
  ReportScheduleList,
  type ReportScheduleListType,
  type ReportScheduleType,
  type UpdateReportScheduleRequestType,
} from './schemas/report-schedule';
import {
  GenerateReportRequest,
  GenerateReportResponse,
} from './schemas/reports';
import {
  Reservation,
  type ReservationListType,
  ReservationsPaginatedResult,
  type ReservationsPaginatedResultType,
  type ReservationsPaginationKey,
  TransactionList,
  type TransactionListType,
  TransactionsPaginatedResult,
  type TransactionsPaginatedResultType,
} from './schemas/reservations';
import {
  AuthResourcesPaginationOptions,
  InviteUserPayload,
  OrganizationInvitationList,
  OrganizationMemberList,
  RoleList,
  UserOrgMemberPayload,
  UserSchema,
} from './schemas/user-management';
import { LogData, UpdateUserPayload } from './schemas/user-mgmt';
import {
  type ApplyValidationPayload,
  type GetMonthlyParkingBillingStatementInput,
  GetValidationPaymentsInput,
  MonthlyParkingBillingStatementSchema,
  SetupValidationsPaymentMethodSchema,
  ValidationBillingQuoteSchema,
  ValidationPaymentsPaginatedSchema,
  ValidationSchema,
  ValidationsPaginatedResult,
  type ValidationsPaginatedResultType,
  type ValidationsPaginationKey,
  type ValidationType,
} from './schemas/validation';
import {
  type VehicleMakeWithModelsList,
  VehicleMakeWithModelsListSchema,
} from './schemas/vehicle';
import {
  type ApplyViolationDiscountInput,
  type LocationDiscountOffersInput,
  locationDiscountOffersPath,
  toApplyViolationDiscountBody,
  toLocationDiscountOffersBody,
  violationDiscountPath,
  type ViolationDiscountState,
  ViolationDiscountStateSchema,
} from './schemas/violation-discount';
import {
  type PaymentLinkDraft,
  PaymentLinkDraftSchema,
  type SendPaymentLinkPayload,
  type SentPaymentLink,
  SentPaymentLinkSchema,
} from './schemas/violation-payment-link';
import {
  type BlockedIssuanceAuditListResult,
  BlockedIssuanceAuditListResultSchema,
  type CheckDuplicateViolationResponse,
  CheckDuplicateViolationResponseSchema,
  type CollectionEventListType,
  CreateViolationInput,
  type DispositionReasonCatalogResponse,
  DispositionReasonCatalogResponseSchema,
  DisputeInvoiceListItem,
  type EvidencePackageResult,
  EvidencePackageResultSchema,
  type FrequentOffendersListResult,
  FrequentOffendersListResultSchema,
  InvoiceType,
  type MarkViolationPaidPayload,
  TriageEnrichmentSchema,
  type TriageEnrichmentType,
  UploadSessionImagesResponseSchema,
  type UploadSessionImagesResponseType,
  UploadSessionSchema,
  type UploadSessionType,
  Violation,
  ViolationList,
  type ViolationListType,
  type ViolationSource,
  ViolationsPaginatedResult,
  ViolationType,
} from './schemas/violations';

type GetReservationsParams = {
  fromDate?: DateTime;
  toDate?: DateTime;
  selectTransactions: boolean;
};

export type GetValidationBillingQuotePayload = {
  location_id: string;
  sub_location_id: string;
  duration: z.infer<typeof zodDuration> | null;
  monthly_quantity: number;
  is_waiving_parking_fee?: boolean;
  // max_validation_mins is required server-side when duration === 'usage'
  // so the worst-case parking fee can be priced against the sublocation's
  // ceiling. We send the live form value (which may differ from the
  // persisted row while the operator is editing) so the quote stays in
  // lock-step with the in-progress configuration. Ignored by the API for
  // fixed durations.
  max_validation_mins?: number | null;
};

export class ApiClient {
  private readonly _client: AxiosInstance;

  constructor(getAccessToken: () => Promise<string>) {
    this._client = axios.create({
      baseURL: getApiBaseUrl(),
      // Fail slow/hung requests instead of waiting indefinitely (axios default is no
      // timeout), which previously left pages stuck on a loading spinner forever.
      timeout: 90_000,
    });

    // Add authorization header to each request
    this._client.interceptors.request.use(async (request) => {
      request.headers.set('Authorization', `Bearer ${await getAccessToken()}`);
      return request;
    });
  }

  async getReservations(
    locationId: string,
    params?: GetReservationsParams & { selectTransactions: false }
  ): Promise<ReservationListType>;
  async getReservations(
    locationId: string,
    params?: GetReservationsParams & { selectTransactions: true }
  ): Promise<TransactionListType>;
  async getReservations(
    locationId: string,
    params: GetReservationsParams = {
      selectTransactions: false,
    }
  ): Promise<ReservationListType | TransactionListType> {
    const url: string = '/transaction';

    const response = await this._client.get(url, {
      params: {
        location_id: locationId,
        start_date: params.fromDate?.toISO(),
        end_date: params.toDate?.toISO(),
        select_transactions: params.selectTransactions ? 't' : 'f',
      },
    });

    if (!response.data) {
      return [];
    }

    if (params.selectTransactions) {
      return TransactionList.parse(response.data);
    }

    return response.data;
  }

  async redeemReservation(reservationID: string) {
    return await this._client.post(`/reservation/presale/redeem`, {
      reservationId: reservationID,
    });
  }

  async getTransactions(
    locationId: string,
    params?: Omit<GetReservationsParams, 'selectTransactions'>
  ) {
    return await this.getReservations(locationId, {
      selectTransactions: true,
      ...params,
    });
  }

  async getPaginatedTransactions({
    locationIds,
    limit,
    reservationId,
    createdAt,
    index,
    phoneNumber,
    ascending = true,
    plate,
    id,
    isDisputed,
    disputeLifecycle,
    disputesQueue,
  }: {
    locationIds: string[];
    limit: number;
    reservationId?: string;
    createdAt?: string;
    index?: number;
    phoneNumber?: string | null;
    locationId?: string;
    ascending?: boolean;
    plate?: string;
    id?: string | null;
    isDisputed?: boolean;
    disputeLifecycle?: 'needs_response' | 'in_review' | 'won' | 'lost';
    disputesQueue?: boolean;
  }): Promise<TransactionsPaginatedResultType> {
    const url = '/transaction/paginated';

    const response = await this._client.get(url, {
      params: removeNullValuesFromObject({
        location_ids: locationIds,
        limit,
        reservationId,
        createdAt,
        index,
        ascending,
        plate,
        phone_number: phoneNumber,
        id,
        is_disputed: isDisputed,
        dispute_lifecycle: disputeLifecycle,
        disputes_queue: disputesQueue,
      }),
    });

    return TransactionsPaginatedResult.parse(response.data);
  }

  async resendEmailCommunication(messageId: string) {
    const response = await this._client.post(
      `/communications/${messageId}/resend`
    );
    return response.data;
  }

  async postValidationApply(
    payload: ApplyValidationPayload
  ): Promise<ValidationType> {
    const response = await this._client.post('/validation/apply', payload);
    return ValidationSchema.parse(response.data);
  }

  async getLocations(): Promise<LocationListType> {
    const response = await this._client.get('/location/configs');
    const raw = Array.isArray(response.data) ? response.data : [];
    return LocationList.parse(raw);
  }

  async listParkpliantGroups(): Promise<ParkpliantGroupType[]> {
    const response = await this._client.get(
      '/monitor/violation/parkpliant/groups'
    );
    const raw = Array.isArray(response.data) ? response.data : [];
    return ParkpliantGroupListSchema.parse(raw);
  }

  async getSubLocations() {
    const response = await this._client.get('/validation/locations');
    return SubLocationsOfLocationListSchema.parse(response.data);
  }

  async updateSubLocation(data: UpdateLocationParams) {
    await this._client.put('/validation/locations', data);
  }

  async getPaginatedValidationPayments(input: GetValidationPaymentsInput) {
    const response = await this._client.get('/validation/billing/payments', {
      params: removeNullValuesFromObject(input),
    });

    return ValidationPaymentsPaginatedSchema.parse(response.data);
  }

  async getValidationBillingQuote(input: GetValidationBillingQuotePayload) {
    const response = await this._client.post(
      '/validation/billing/quote',
      input
    );
    return ValidationBillingQuoteSchema.parse(response.data);
  }

  async getMonthlyParkingBillingStatement(
    input: GetMonthlyParkingBillingStatementInput
  ) {
    const response = await this._client.get(
      '/validation/billing/monthly-parking',
      { params: input }
    );
    return MonthlyParkingBillingStatementSchema.parse(response.data);
  }

  async downloadMonthlyParkingBillingStatementPdf(
    input: GetMonthlyParkingBillingStatementInput
  ) {
    const response = await this._client.get(
      '/validation/billing/monthly-parking/pdf',
      { params: input, responseType: 'blob' }
    );
    return response.data as Blob;
  }

  async setupNewValidationPaymentMethod() {
    const response = await this._client.post('/validation/billing/method');
    return SetupValidationsPaymentMethodSchema.parse(response.data);
  }

  async createLocation(location: CreateLocationPayloadSchemaType) {
    const response = await this._client.post('/location/configs', location);
    return response.data;
  }

  async getViolations(
    locationId: string,
    options?: { includeImages?: boolean }
  ): Promise<ViolationListType> {
    const response = await this._client.get(
      `/monitor/violations/${locationId}`,
      {
        params: removeNullValuesFromObject({
          include_images: options?.includeImages,
        }),
      }
    );
    return ViolationList.parse(response.data);
  }

  async getViolationById(violationId: string): Promise<ViolationType> {
    const response = await this._client.get(
      `/monitor/violation/${violationId}`
    );
    return Violation.parse(response.data);
  }

  async getPaginatedViolations({
    locationIds,
    limit,
    ascending = false,
    statuses,
    plate,
    plateState,
    violationId,
    source,
    hasDispute,
    createdDateGTE,
    createdDateLTE,
    startKey,
    signal,
    includeImages,
  }: {
    locationIds: string[];
    limit: number;
    ascending?: boolean;
    statuses?: string[];
    plate?: string | null;
    plateState?: PlateStateAbbreviation | null;
    violationId?: string | null;
    source?: ViolationSource | null;
    hasDispute?: boolean | null;
    createdDateGTE?: DateTime<true> | null;
    createdDateLTE?: DateTime<true> | null;
    startKey?: {
      id: string;
      createdAt: string;
    };
    signal?: AbortSignal;
    includeImages?: boolean;
  }) {
    const response = await this._client.get('/monitor/violations/list', {
      params: removeNullValuesFromObject({
        location_ids: locationIds,
        limit,
        ascending,
        statuses: statuses,
        plate,
        plate_state: plateState,
        violation_id: violationId,
        source,
        has_dispute: hasDispute,
        created_date_gte: createdDateGTE?.toISO(),
        created_date_lte: createdDateLTE?.toISO(),
        id: startKey?.id,
        createdAt: startKey?.createdAt,
        include_images: includeImages,
      }),
      signal,
    });

    return ViolationsPaginatedResult.parse(response.data);
  }

  async getViolationTriageEnrichment(params: {
    locationIds: string[];
    violationLocationId: string;
    plate: string;
    plateState?: string;
  }): Promise<TriageEnrichmentType> {
    const response = await this._client.get(
      '/monitor/violations/triage-enrichment',
      {
        params: removeNullValuesFromObject({
          location_ids: params.locationIds,
          violation_location_id: params.violationLocationId,
          plate: params.plate,
          plate_state: params.plateState,
        }),
      }
    );
    return TriageEnrichmentSchema.parse(response.data);
  }

  async getCameraEventByIds(eventIds: string[]): Promise<CameraEventListType> {
    if (!eventIds.length) {
      return [];
    }
    const ids = eventIds.map((id) => `ids=${id}`).join('&');
    const response = await this._client.get(
      `/monitor/camera/eventsById?${ids}`
    );

    return CameraEventList.parse(response.data);
  }

  //getNoticesByViolationId
  async getCollectionEventsByViolationId(
    violationId: string
  ): Promise<CollectionEventListType> {
    const response = await this._client.get(
      `monitor/violation/parkpliant/events/${violationId}`
    );
    return response.data;
  }

  async getCollectionEvents(): Promise<CollectionEventListType> {
    const response = await this._client.get(
      `monitor/violation/parkpliant/events`
    );
    return response.data;
  }

  async getCameraEvents({
    locationId,
    ascending,
    limit,
    eventId,
    capturedAtUnix,
    since,
    until,
    plate,
    direction,
    serialNumber,
  }: {
    locationId: string;
    ascending: boolean;
    limit: number;
    eventId?: string;
    capturedAtUnix?: number;
    since?: string | null;
    until?: string | null;
    plate?: string | null;
    direction?: 'in' | 'out' | 'unknown' | null;
    serialNumber?: string | null;
  }) {
    const response = await this._client.get(`/monitor/camera/events`, {
      params: removeNullValuesFromObject({
        locationID: locationId,
        limit,
        ascending,
        since,
        until,
        plate,
        direction,
        serial_number: serialNumber,
        eventId,
        capturedAtUnix,
      }),
    });

    return CameraEventsPaginatedResult.parse(response.data);
  }

  /**
   * Confirms a camera event by sending mapped fields to the API.
   *
   * Field mapping (portal → API):
   * - `locationID` → `locationId`
   * - `moveDirection` → `direction`
   *
   * @returns `void` on success (API returns 204 No Content),
   *          `'in camera event not found'` when an "out" event lacks a confirmed "in" event.
   */
  async confirmCameraEvent(
    data: CameraEventType
  ): Promise<void | 'in camera event not found'> {
    const plateState = data.plateState?.trim() || undefined;

    const payload = ConfirmCameraEventRequest.parse({
      eventId: data.eventId,
      plate: data.plate,
      locationId: data.locationID,
      direction: data.moveDirection,
      ...(plateState && { plateState }),
    });

    try {
      const response = await this._client.post(
        '/monitor/camera/event/confirm',
        payload
      );
      return response.data;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.message?.includes('in camera event not found')
      ) {
        return 'in camera event not found';
      }
      throw error;
    }
  }

  async getViolationAuditRecord(
    violationId: string,
    startKey?: { violationId?: string; updatedAt?: string },
    limit = 10,
    ascending = false
  ) {
    let startKeyParam: string | null = null;
    if (startKey) {
      startKeyParam = encodeURIComponent(JSON.stringify(startKey));
    }

    const response = await this._client.get(
      `monitor/violation/${violationId}/audit-trail?limit=${limit}&ascending=${ascending}&startKey=${startKeyParam}`
    );
    return response.data;
  }

  async updateViolation(
    data: ViolationType & { acknowledgeLateMailing?: boolean }
  ) {
    const supportedData = {
      violationId: data.violationId,
      status: data.status,
      acknowledgeLateMailing: data.acknowledgeLateMailing,
      plate: data.plate,
      plateState: data.plateState,
      vehicleMake: data.vehicleMake,
      vehicleModel: data.vehicleModel,
      vehicleColor: data.vehicleColor,
      notes: data.notes,
      externalNotes: data.externalNotes,
      reason: data.reason,
      discountValidDays: data.discountValidDays,
      locationId: data.locationId,
      pricingSchedule: data.pricingSchedule,
      waiveReason: data.waiveReason,
      // Coerce empty string -> undefined so the field is omitted from the
      // request body. Sending an explicit empty string for a non-'other'
      // slug would leak through the validator and clobber any preserved
      // legacy detail on the row.
      waiveReasonDetail: data.waiveReasonDetail || undefined,
      voidReason: data.voidReason,
      voidReasonDetail: data.voidReasonDetail || undefined,
      cityHearingAt: data.cityHearingAt?.toISO() ?? null,
      hearingOfficerFees: data.hearingOfficerFees ?? undefined,
    };
    const response = await this._client.put(
      '/monitor/violation/update',
      supportedData
    );
    return response.data;
  }

  async dismissViolation(
    violationId: string,
    voidReason: string,
    reason?: string
  ) {
    const response = await this._client.put(
      `/monitor/violation/void/${violationId}`,
      {
        void_reason: voidReason,
        ...(reason && { reason }),
      }
    );
    return response.data;
  }

  async getViolationDiscount(
    violationId: string
  ): Promise<ViolationDiscountState> {
    const response = await this._client.get(violationDiscountPath(violationId));
    return ViolationDiscountStateSchema.parse(response.data);
  }

  async applyViolationDiscount(
    violationId: string,
    input: ApplyViolationDiscountInput
  ): Promise<ViolationDiscountState> {
    const response = await this._client.post(
      violationDiscountPath(violationId),
      toApplyViolationDiscountBody(input)
    );
    return ViolationDiscountStateSchema.parse(response.data);
  }

  async removeViolationDiscount(
    violationId: string
  ): Promise<ViolationDiscountState> {
    const response = await this._client.delete(
      violationDiscountPath(violationId)
    );
    return ViolationDiscountStateSchema.parse(response.data);
  }

  async listEnforcementStatements(params: {
    period: string;
    limit: number;
    offset: number;
  }): Promise<EnforcementStatement[]> {
    const response = await this._client.get(
      '/internal/revenue/enforcement-payouts/statements',
      { params }
    );
    return EnforcementStatementListSchema.parse(response.data).statements;
  }

  async saveEnforcementStatements(
    period: string
  ): Promise<SaveEnforcementStatementsResult> {
    const response = await this._client.post(
      '/internal/revenue/enforcement-payouts/statements',
      undefined,
      { params: { period } }
    );
    return SaveEnforcementStatementsResultSchema.parse(response.data);
  }

  async approveEnforcementStatement(
    statementId: string
  ): Promise<EnforcementStatement> {
    const response = await this._client.post(
      `/internal/revenue/enforcement-payouts/statements/${statementId}/approve`
    );
    return EnforcementStatementSchema.parse(response.data);
  }

  async discardEnforcementStatement(statementId: string): Promise<void> {
    await this._client.delete(
      `/internal/revenue/enforcement-payouts/statements/${statementId}`
    );
  }

  async updateLocationDiscountOffers(
    locationId: string,
    offers: LocationDiscountOffersInput
  ): Promise<void> {
    await this._client.put(
      locationDiscountOffersPath(locationId),
      toLocationDiscountOffersBody(offers)
    );
  }

  async getDispositionReasonCatalog(): Promise<DispositionReasonCatalogResponse> {
    const response = await this._client.get(
      '/monitor/violations/reasons/disposition'
    );
    return DispositionReasonCatalogResponseSchema.parse(response.data);
  }

  async generateEvidencePackage(
    violationId: string
  ): Promise<EvidencePackageResult> {
    const response = await this._client.post(
      `/monitor/violation/${violationId}/evidence-package`
    );
    return EvidencePackageResultSchema.parse(response.data);
  }

  async getViolationPaymentLink(
    violationId: string,
    signal?: AbortSignal
  ): Promise<PaymentLinkDraft> {
    const response = await this._client.get(
      `/monitor/violation/${encodeURIComponent(violationId)}/payment-link`,
      { signal }
    );
    return PaymentLinkDraftSchema.parse(response.data);
  }

  async sendViolationPaymentLink(
    violationId: string,
    payload: SendPaymentLinkPayload
  ): Promise<SentPaymentLink> {
    const response = await this._client.post(
      `/monitor/violation/${encodeURIComponent(violationId)}/payment-link/send`,
      payload
    );
    return SentPaymentLinkSchema.parse(response.data);
  }

  /**
   * Create a new violation for a location
   * Uses multipart/form-data to support image uploads
   */
  async createViolation(
    locationId: string,
    data: CreateViolationInput
  ): Promise<ViolationType> {
    const formData = new FormData();

    // Append text fields
    formData.append('plate', data.plate.toUpperCase());
    formData.append('plateState', data.plateState);
    formData.append('vehicleMake', data.vehicleMake);
    formData.append('vehicleModel', data.vehicleModel);
    formData.append('vehicleColor', data.vehicleColor);
    formData.append('reason', data.reason);

    if (data.notes) {
      formData.append('notes', data.notes);
    }

    if (data.externalNotes) {
      formData.append('externalNotes', data.externalNotes);
    }

    if (data.blockFaceId) {
      formData.append('blockFaceId', data.blockFaceId);
    }

    if (data.bypassChalking) {
      formData.append('bypassChalking', 'true');
    }

    if (data.bypassDailyViolationLimit) {
      formData.append('bypassDailyViolationLimit', 'true');
    }

    if (data.blockedAuditEntryId != null && data.blockedAuditEntryId > 0) {
      formData.append('blockedAuditEntryId', String(data.blockedAuditEntryId));
    }

    // Append officer fields if provided (for public/government enforcement)
    if (data.officerName) {
      formData.append('officerName', data.officerName);
    }
    if (data.address) {
      formData.append('address', data.address);
    }

    if (data.uploadToken) {
      formData.append('uploadToken', data.uploadToken);
    } else if (data.images && data.images.length > 0) {
      data.images.forEach((file) => {
        formData.append('images', file);
      });
    }

    // Don't set Content-Type header - axios will automatically set it
    // with the correct boundary for multipart/form-data
    const response = await this._client.post(
      `/monitor/violations/${locationId}`,
      formData
    );

    return Violation.parse(response.data);
  }

  async createChalk(data: CreateChalkInput): Promise<ChalkType> {
    const formData = new FormData();
    formData.append('locationId', data.locationId);
    formData.append('plate', data.plate.toUpperCase());
    formData.append('plateState', data.plateState);
    formData.append('vehicleMake', data.vehicleMake);
    formData.append('vehicleModel', data.vehicleModel);
    formData.append('vehicleColor', data.vehicleColor);
    formData.append('reason', data.reason);

    if (data.blockFaceId) {
      formData.append('blockFaceID', data.blockFaceId);
    }

    if (data.images && data.images.length > 0) {
      data.images.forEach((file) => {
        formData.append('images', file);
      });
    }

    const response = await this._client.post('/monitor/chalks', formData);

    return ChalkSchema.parse(response.data);
  }

  async attachImagesToViolation(
    violationId: string,
    uploadToken: string
  ): Promise<void> {
    await this._client.post(`/monitor/violation/${violationId}/peo-images`, {
      uploadToken,
    });
  }

  async createUploadSession(locationId: string): Promise<UploadSessionType> {
    const response = await this._client.post(
      '/monitor/violations/upload-sessions',
      { location_id: locationId }
    );
    return UploadSessionSchema.parse(response.data);
  }

  async getUploadSessionImages(
    token: string
  ): Promise<UploadSessionImagesResponseType> {
    const response = await this._client.get(
      `/monitor/violations/upload-sessions/${token}/images`
    );
    return UploadSessionImagesResponseSchema.parse(response.data);
  }

  /**
   * Check for duplicate violations before creating a new one.
   * Uses fail-open pattern - errors should be handled by caller to allow creation.
   */
  async checkDuplicateViolation(
    locationId: string,
    params: {
      plate: string;
      plateState: string;
      reason: string;
    }
  ): Promise<CheckDuplicateViolationResponse> {
    const response = await this._client.get(
      `/monitor/violations/${locationId}/check-duplicate`,
      {
        params: {
          plate: params.plate,
          plate_state: params.plateState,
          reason: params.reason,
        },
      }
    );

    return CheckDuplicateViolationResponseSchema.parse(response.data);
  }

  /**
   * List blocked violation issuance attempts (audit) for a location.
   * Support can review and waive (allow creation) from the UI.
   */
  async listBlockedIssuanceAudit(
    locationId: string,
    params?: {
      plate?: string;
      plate_state?: string;
      source?: string;
      created_after?: string;
      created_before?: string;
      limit?: number;
      ascending?: boolean;
      startKey?: { id: number; createdAt: string };
    }
  ): Promise<BlockedIssuanceAuditListResult> {
    const { startKey, ...rest } = params ?? {};
    const response = await this._client.get(
      `/monitor/violations/${locationId}/blocked-issuance-audit`,
      {
        params: removeNullValuesFromObject({
          ...rest,
          id: startKey?.id,
          createdAt: startKey?.createdAt,
        }),
      }
    );
    const parsed = BlockedIssuanceAuditListResultSchema.safeParse(
      response.data
    );
    if (!parsed.success) {
      console.warn(
        '[listBlockedIssuanceAudit] Response shape mismatch:',
        parsed.error.flatten()
      );
      return {
        pagination: { hasMore: false, count: 0 },
        data: [],
        debugInfo: null,
      };
    }
    return parsed.data;
  }

  /**
   * List frequent offenders (plates with most blocked attempts) for a location.
   * Sorted by attempt_count DESC (highest first).
   */
  async listFrequentOffenders(
    locationId: string,
    params?: {
      created_after?: string;
      created_before?: string;
      min_attempts?: number;
      limit?: number;
      ascending?: boolean;
      startKey?: { attemptCount: number; plate: string; reason: string };
    }
  ): Promise<FrequentOffendersListResult> {
    const { startKey, ...rest } = params ?? {};
    const response = await this._client.get(
      `/monitor/violations/${locationId}/blocked-issuance-audit/frequent-offenders`,
      {
        params: removeNullValuesFromObject({
          ...rest,
          attemptCount: startKey?.attemptCount,
          plate: startKey?.plate,
          reason: startKey?.reason,
        }),
      }
    );
    const parsed = FrequentOffendersListResultSchema.safeParse(response.data);
    if (!parsed.success) {
      console.warn(
        '[listFrequentOffenders] Response shape mismatch:',
        parsed.error.flatten()
      );
      return {
        pagination: { hasMore: false, count: 0 },
        data: [],
        debugInfo: null,
      };
    }
    return parsed.data;
  }

  async getLocationById(
    locationId: string,
    options: { withEnforcement?: boolean } = {}
  ): Promise<LocationType> {
    const response = await this._client.get(`/location/configs/${locationId}`, {
      params: {
        ...(options.withEnforcement && {
          enforcement: options.withEnforcement,
        }),
      },
    });

    if (
      !response.data ||
      !Array.isArray(response.data) ||
      !response.data.length
    ) {
      throw new Error(`Location with id ${locationId} was not found`);
    }

    return LocationSchema.parse(response.data[0]);
  }

  async updateLocation(
    updatedLocation: LocationType,
    options: { expectedUpdatedAt?: DateTime | null } = {}
  ) {
    // TODO: this stuff is really messy and we shouldn't add our transformation logic to the api client.
    // API Client has to be pure. This needs to be refactored asap
    if (
      updatedLocation?.pricing_schedule?.weekday_rules &&
      Array.isArray(updatedLocation?.pricing_schedule?.weekday_rules)
    ) {
      // @ts-expect-error TODO fix this. LocationType defines weekday_rules as an array but here it's an object
      // because that's how API accepts the weekday rules when updating/creating a location
      updatedLocation.pricing_schedule.weekday_rules =
        updatedLocation.pricing_schedule.weekday_rules.reduce((acc, item) => {
          const { weekDay, ...pricingSchema } = item;
          return { ...acc, [weekDay]: pricingSchema };
        }, {});
    }

    if (
      'validation_settings' in updatedLocation &&
      updatedLocation.validation_settings?.validation_pricing &&
      !updatedLocation.validation_settings.validation_pricing.items?.length
    ) {
      updatedLocation.validation_settings.validation_pricing = null;
    }

    if (
      updatedLocation.type === LocationKind.REGULAR &&
      updatedLocation?.block_faces?.length
    ) {
      for (const blockFace of updatedLocation.block_faces) {
        if (!Array.isArray(blockFace?.pricing_schedule?.weekday_rules)) {
          continue;
        }
        // @ts-expect-error TODO fix this
        blockFace.pricing_schedule.weekday_rules =
          blockFace.pricing_schedule.weekday_rules.reduce((acc, item) => {
            const { weekDay, ...pricingSchema } = item;
            return { ...acc, [weekDay]: pricingSchema };
          }, {});
      }
    }
    return await this._client.put(
      `/location/configs/${updatedLocation.locationId}`,
      {
        ...updatedLocation,
        ...(options.expectedUpdatedAt && {
          expected_updated_at: options.expectedUpdatedAt
            .toUTC()
            .toISO({ suppressMilliseconds: true }),
        }),
      }
    );
  }

  async getPermits(
    locationId: string,
    options?: {
      permitTypes?: string[];
      statuses?: string[];
      subLocationIds?: string[];
      plate?: string;
      plateState?: string;
      fuzzySearch?: boolean;
      limit?: number;
      withinLocationGroup?: boolean;
    }
  ) {
    const params: Record<string, string | string[] | number | boolean> = {};

    if (options?.permitTypes?.length) {
      params['permit_type'] = options.permitTypes;
    }
    if (options?.statuses?.length) {
      params['status'] = options.statuses;
    }
    if (options?.subLocationIds?.length) {
      params['sub_location_ids'] = options.subLocationIds;
    }
    if (options?.plate) {
      params['plate'] = options.plate;
    }
    if (options?.plateState) {
      params['plate_state'] = options.plateState;
    }
    if (options?.fuzzySearch) {
      params['fuzzy_search'] = options.fuzzySearch;
    }
    if (options?.limit) {
      params['limit'] = options.limit;
    }
    if (options?.withinLocationGroup) {
      params['within_location_group'] = true;
    }

    const response = await this._client.get(`/location/permit/${locationId}`, {
      params: params,
      paramsSerializer: {
        indexes: null,
      },
    });
    return PermitList.parse(response.data);
  }

  // getPermitsBulk hits the multi-location bulk endpoint introduced for the Violation
  // Triage entry page (ENG-1311). Single round-trip across many location_ids; replaces
  // the legacy per-location useQueries fan-out via getPermits().
  //
  // Backend binds form:"location_ids[]" / form:"status[]" / form:"permit_type[]". Keys
  // here are bare (no manual `[]` suffix) — axios's default serializer appends `[]` to
  // array-valued params automatically, matching the bracketed form tags. Mirrors how
  // getPaginatedViolations passes location_ids above.
  async getPermitsBulk(params: {
    locationIds: string[];
    statuses?: string[];
    permitTypes?: string[];
    limit?: number;
  }) {
    const response = await this._client.get('/location/permit/list', {
      params: removeNullValuesFromObject({
        location_ids: params.locationIds,
        status: params.statuses,
        permit_type: params.permitTypes,
        limit: params.limit,
      }),
    });
    return PermitList.parse(response.data);
  }

  // getPermitsPaginated hits the unified paginated GET /permits endpoint
  // shipped with ENG-1532. Cursor (createdAtMillis, permitId) is sent as flat
  // top-level params, matching how Gin's nested struct binding reads
  // pagination.Options[PermitStartKey] on the server.
  async getPermitsPaginated(params: {
    locationIds: string[];
    plate?: string;
    plateState?: string;
    fuzzySearchByPlate?: boolean;
    withinLocationGroup?: boolean;
    startDate?: string;
    endDate?: string;
    permitTypes?: string[];
    statuses?: string[];
    subLocationIds?: string[];
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    limit: number;
    ascending?: boolean;
    startKey?: PermitPaginationKey;
  }): Promise<PermitsPaginatedResultType> {
    const response = await this._client.get('/permits', {
      params: removeNullValuesFromObject({
        location_ids: params.locationIds,
        plate: params.plate,
        plate_state: params.plateState,
        fuzzy_search_by_plate: params.fuzzySearchByPlate,
        // The plate filter is always a contains match (ENG-2334) - callers never expose
        // an operator choice, so this is derived rather than taken as a param. Only set
        // alongside a plate fragment and never together with fuzzy search, which the
        // server treats as mutually exclusive precedence, not an additive filter.
        substring_search_by_plate:
          params.plate && !params.fuzzySearchByPlate ? true : undefined,
        within_location_group: params.withinLocationGroup,
        start_date: params.startDate,
        end_date: params.endDate,
        permit_type: params.permitTypes,
        status: params.statuses,
        sub_location_ids: params.subLocationIds,
        first_name: params.firstName,
        last_name: params.lastName,
        email: params.email,
        phone: params.phone,
        limit: params.limit,
        ascending: params.ascending,
        createdAtMillis: params.startKey?.createdAtMillis,
        permitId: params.startKey?.permitId,
      }),
    });
    return PermitsPaginatedResult.parse(response.data);
  }

  // getPermitCoverage hits the dedicated point-in-time GET /permits/coverage
  // endpoint (ENG-2264). Unlike GET /permits - a "now" list that filters
  // deleted_at IS NULL - this answers "what covered this plate at the violation
  // instant" and is the only permit read that can reach soft-deleted rows.
  //
  // `at` is required: the server 400s without it, there is no implicit "now".
  // Plate state is deliberately NOT sent - a citation's stored state can be the
  // camera's forced value (ENG-2201), so the lookup matches on plate number
  // alone and returns each permit's real state for display.
  async getPermitCoverage(params: {
    locationIds: string[];
    plate: string;
    at: string;
    withinLocationGroup?: boolean;
    // Widen the match to Levenshtein distance <= 2 and surface permits created
    // after the instant, distance-ordered. Sent only when true: the truthiness
    // filter below drops a false/undefined value, so exact mode never emits the
    // `fuzzy` param and its request shape is unchanged.
    fuzzy?: boolean;
  }): Promise<PermitCoverageResultType> {
    const response = await this._client.get('/permits/coverage', {
      params: removeNullValuesFromObject({
        location_ids: params.locationIds,
        plate: params.plate,
        at: params.at,
        within_location_group: params.withinLocationGroup,
        fuzzy: params.fuzzy,
      }),
    });
    return PermitCoverageResult.parse(response.data);
  }

  // getValidationsPaginated hits the unified paginated GET /validations
  // endpoint shipped with ENG-1533. Cursor (locationId, validationId,
  // createdAt) is sent as flat top-level params.
  async getValidationsPaginated(params: {
    locationIds: string[];
    plate?: string;
    plateState?: string;
    fuzzySearchByPlate?: boolean;
    withinLocationGroup?: boolean;
    startDate?: string;
    endDate?: string;
    statuses?: string[];
    subLocationIds?: string[];
    blockFaceId?: string;
    limit: number;
    ascending?: boolean;
    startKey?: ValidationsPaginationKey;
  }): Promise<ValidationsPaginatedResultType> {
    const response = await this._client.get('/validations', {
      params: removeNullValuesFromObject({
        location_ids: params.locationIds,
        plate: params.plate,
        plate_state: params.plateState,
        fuzzy_search_by_plate: params.fuzzySearchByPlate,
        // The plate filter is always a contains match (ENG-2334) - callers never expose
        // an operator choice, so this is derived rather than taken as a param. Only set
        // alongside a plate fragment and never together with fuzzy search, which the
        // server treats as mutually exclusive precedence, not an additive filter.
        substring_search_by_plate:
          params.plate && !params.fuzzySearchByPlate ? true : undefined,
        within_location_group: params.withinLocationGroup,
        start_date: params.startDate,
        end_date: params.endDate,
        status: params.statuses,
        sub_location_ids: params.subLocationIds,
        block_face_id: params.blockFaceId,
        limit: params.limit,
        ascending: params.ascending,
        locationId: params.startKey?.locationId,
        validationId: params.startKey?.validationId,
        createdAt: params.startKey?.createdAt,
      }),
    });
    return ValidationsPaginatedResult.parse(response.data);
  }

  // getReservationsPaginated hits the unified paginated GET /reservations
  // endpoint shipped with ENG-1534. Cursor (createdAtMillis, reservationId)
  // is sent as flat top-level params.
  async getReservationsPaginated(params: {
    locationIds: string[];
    plate?: string;
    plateState?: string;
    fuzzySearchByPlate?: boolean;
    withinLocationGroup?: boolean;
    startDate?: string;
    endDate?: string;
    statuses?: string[];
    limit: number;
    ascending?: boolean;
    startKey?: ReservationsPaginationKey;
  }): Promise<ReservationsPaginatedResultType> {
    const response = await this._client.get('/reservations', {
      params: removeNullValuesFromObject({
        location_ids: params.locationIds,
        plate: params.plate,
        plate_state: params.plateState,
        fuzzy_search_by_plate: params.fuzzySearchByPlate,
        within_location_group: params.withinLocationGroup,
        start_date: params.startDate,
        end_date: params.endDate,
        status: params.statuses,
        limit: params.limit,
        ascending: params.ascending,
        createdAtMillis: params.startKey?.createdAtMillis,
        reservationId: params.startKey?.reservationId,
      }),
    });
    return ReservationsPaginatedResult.parse(response.data);
  }

  async getPermitById(permitId: string) {
    const response = await this._client.get(`/location/permits/${permitId}`);
    return Permit.parse(response.data);
  }

  async getPermitVisits({
    permitId,
    limit,
    ascending = false,
    startKey,
    from,
    to,
    signal,
  }: {
    permitId: string;
    limit: number;
    ascending?: boolean;
    startKey?: PermitVisitStartKey;
    from?: DateTime<true> | null;
    to?: DateTime<true> | null;
    signal?: AbortSignal;
  }) {
    const response = await this._client.get(
      `/location/permits/${permitId}/visits`,
      {
        // removeEmpty (vs removeNullValuesFromObject) preserves ascending=false
        // — the descending toggle would otherwise omit the param and rely on
        // the server default, which is a fragile contract.
        params: removeEmpty({
          limit,
          ascending,
          // pkg/pagination binds StartKey fields via gin form tags; the server
          // expects them flat alongside limit, not nested under a single key.
          entryAtMillis: startKey?.entryAtMillis,
          eventId: startKey?.eventId,
          from: from?.toISO(),
          to: to?.toISO(),
        }),
        signal,
      }
    );
    return PermitVisitsResultSchema.parse(response.data);
  }

  async deletePermit(locationId: string, permitId: string) {
    const response = await this._client.delete(
      `/location/permit/${locationId}/${permitId}`
    );
    return response.data;
  }

  async updatePermitFields(permitId: string, data: UpdatePermitSchemaType) {
    const response = await this._client.put(
      `/location/permits/${permitId}`,
      data
    );
    return Permit.parse(response.data);
  }

  async createPermit(permit: CreatePermitSchemaType) {
    const data: Record<string, unknown> = {
      location_id: permit?.locationId,
      permit_type: permit.permit_type,
      vehicle: permit.vehicle,
      first_name: permit.first_name,
      last_name: permit.last_name,
      phone: permit.phone,
      email: permit.email,
      company: permit.company,
    };
    // Picker emits ISO with offset (anchored in the user's preferred zone), so the
    // string already encodes a precise UTC instant. The server's `starts_at` /
    // `expires_at` fields accept any RFC3339 offset, so pass through as-is.
    if (permit.permit_type === 'temporary') {
      if (permit.starts_at?.trim()) {
        data.starts_at = permit.starts_at.trim();
      }
      if (permit.expires_at?.trim()) {
        data.expires_at = permit.expires_at.trim();
      }
    } else if (permit.expires_at?.trim()) {
      data.expires_at = permit.expires_at.trim();
    }
    if (permit.permit_coverage) {
      data.permit_coverage = permit.permit_coverage;
    }
    if (permit.location_group_key?.trim()) {
      data.location_group_key = permit.location_group_key.trim();
    }
    if (permit.location_group_member_ids?.length) {
      data.location_group_member_ids = permit.location_group_member_ids;
    }
    const response = await this._client.post('/location/permit', data);
    return response.data;
  }

  async createQuickPermit(permit: QuickPermitFormSchemaType) {
    const phoneDigits = permit.phone.replace(/\D/g, '');
    let phoneE164: string | undefined;
    if (phoneDigits.length > 0) {
      const parsed = parsePhoneNumberFromString(permit.phone.trim(), 'US');
      phoneE164 = parsed?.format('E.164');
    }

    const data: Record<string, unknown> = {
      location_id: permit.locationId,
      permit_type: permit.permit_type,
      first_name: permit.first_name,
      last_name: permit.last_name,
      email: permit.email,
      ...(permit.company?.trim() ? { company: permit.company.trim() } : {}),
      vehicle: {
        plate: permit.vehicle.plate,
        state: permit.vehicle.state,
        make: permit.vehicle.make || undefined,
        model: permit.vehicle.model || undefined,
        color: permit.vehicle.color || undefined,
      },
    };
    if (phoneE164) {
      data.phone = phoneE164;
    }
    if (permit.permit_type === 'temporary') {
      data.starts_at = permit.starts_at;
      data.expires_at = permit.expires_at;
    }
    if (permit.permit_coverage) {
      data.permit_coverage = permit.permit_coverage;
    }
    if (permit.location_group_key?.trim()) {
      data.location_group_key = permit.location_group_key.trim();
    }
    if (permit.location_group_member_ids?.length) {
      data.location_group_member_ids = permit.location_group_member_ids;
    }
    const response = await this._client.post('/location/permit/quick', data);
    return response.data;
  }

  async updatePermitStatus(
    locationId: string,
    permitId: string,
    status: string
  ) {
    const data = {
      status,
    };
    const response = await this._client.put(
      `/location/permit/status/${locationId}/${permitId}`,
      data
    );
    return response.data;
  }

  async cancelSubscription(
    stripeSubscriptionId: string,
    cancelSubscriptionPayload: CancelSubscriptionSchemaType
  ) {
    const response = await this._client.post(
      `/stripe/billing/subscriptions/${stripeSubscriptionId}/cancel`,
      cancelSubscriptionPayload
    );
    return response.data;
  }

  async performRefund(
    paymentIntentId: string,
    amount?: number,
    reason?: 'duplicate' | 'fraudulent' | 'requested_by_customer',
    internalNotes?: string
  ) {
    const response = await this._client.post('/stripe/transaction/refund', {
      paymentIntent: paymentIntentId,
      ...(amount !== undefined && { amount }),
      ...(reason && { reason }),
      ...(internalNotes && { internalNotes }),
    });
    return response.data;
  }

  async getInvoice(invoiceId: string) {
    const response = await this._client.get(`/stripe/invoice/${invoiceId}`);
    return response.data as InvoiceType;
  }

  /** All enforcement invoices (used by Disputes queue to include violation payment disputes). */
  async listInvoices({
    isDisputed,
    disputeLifecycle,
  }: {
    isDisputed?: boolean;
    disputeLifecycle?: 'needs_response' | 'in_review' | 'won' | 'lost';
  } = {}): Promise<DisputeInvoiceListItem[]> {
    const response = await this._client.get('/stripe/invoice', {
      params: removeNullValuesFromObject({
        is_disputed: isDisputed,
        dispute_lifecycle: disputeLifecycle,
      }),
    });
    const raw = Array.isArray(response.data) ? response.data : [];
    const parsed: DisputeInvoiceListItem[] = [];
    for (const item of raw) {
      const result = DisputeInvoiceListItem.safeParse(item);
      if (result.success) {
        parsed.push(result.data);
      }
    }
    if (parsed.length === 0 && raw.length > 0) {
      console.warn(
        '[listInvoices] failed to parse any invoices',
        raw.slice(0, 1)
      );
    }
    return parsed;
  }

  async regenerateValidationCodes(locationId: string, subLocationId: string) {
    return await this._client.post('/validation/rotate', null, {
      params: {
        locationId: locationId,
        sublocationId: subLocationId,
        type: 'access,renewal',
      },
    });
  }

  async getRotationSchedule(
    locationId: string,
    subLocationId: string
  ): Promise<RotationScheduleType> {
    const response = await this._client.get(
      `/validation/${locationId}/${subLocationId}/rotation`
    );
    return RotationScheduleSchema.parse(response.data);
  }

  async updateRotationSchedule(
    locationId: string,
    subLocationId: string,
    schedule: Omit<RotationScheduleType, 'last_scheduled_rotation_at'>
  ): Promise<RotationScheduleType> {
    const response = await this._client.put(
      `/validation/${locationId}/${subLocationId}/rotation`,
      schedule
    );
    return RotationScheduleSchema.parse(response.data);
  }

  async getInvoiceById(invoiceId: string): Promise<InvoiceType> {
    const response = await this._client.get(`/stripe/invoice/${invoiceId}`);
    return response.data;
  }

  async getCustomers(
    {
      includeRelatedBankAccounts,
    }: {
      includeRelatedBankAccounts: boolean;
    } = { includeRelatedBankAccounts: false }
  ): Promise<CustomerListType> {
    const response = await this._client.get('/customers', {
      params: {
        include_bank_accounts: includeRelatedBankAccounts,
      },
    });
    try {
      return CustomerList.parse(response.data);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getCustomerById(customerId: string): Promise<CustomerType> {
    const response = await this._client.get(`/customers/${customerId}`);
    return Customer.parse(response.data);
  }

  async getCustomerBankAccounts(
    customerId: string
  ): Promise<BankAccountListType> {
    const response = await this._client.get('/bank_accounts', {
      params: {
        customer_id: customerId,
      },
    });
    return BankAccountList.parse(response.data);
  }

  async getBankAccountById(bankAccountId: string): Promise<BankAccountType> {
    const response = await this._client.get(`/bank_accounts/${bankAccountId}`);
    return BankAccount.parse(response.data);
  }

  /**
   * Partial update of a bank account's name and/or branding. Uses
   * multipart/form-data to carry the optional logo file; only provided fields
   * are sent so omitted fields are left unchanged on the server.
   */
  async updateBankAccountBranding(
    bankAccountId: string,
    branding: UpdateBankAccountBrandingInput
  ): Promise<BankAccountType> {
    const formData = new FormData();

    const textFields: Array<[string, string | undefined]> = [
      ['name', branding.name],
      ['public_name', branding.public_name],
      ['brand_color', branding.brand_color],
      ['accent_color', branding.accent_color],
      ['support_email', branding.support_email],
      ['support_phone', branding.support_phone],
      ['support_url', branding.support_url],
    ];
    textFields.forEach(([key, value]) => {
      if (value != null) {
        formData.append(key, value);
      }
    });

    if (branding.logo) {
      formData.append('logo', branding.logo);
    }

    // Don't set Content-Type - axios sets the multipart boundary automatically.
    const response = await this._client.put(
      `/bank_accounts/${bankAccountId}`,
      formData
    );
    return BankAccount.parse(response.data);
  }

  async markViolationPaid(payload: MarkViolationPaidPayload): Promise<void> {
    await this._client.post('/stripe/pay', payload);
  }

  async updateCustomer(
    customerId: string,
    customer: CustomerCreateSchemaType
  ): Promise<CustomerType> {
    const response = await this._client.put(`/customers/${customerId}`, {
      name: customer.name,
      address: customer.address,
      email: customer.email,
      phone: customer.phoneNumber,
      payout_mode: customer.payout_mode,
      enforcement_payout_mode: customer.enforcement_payout_mode,
      timezone: customer.timezone,
      net_invoice_payout_percent: customer.netInvoicePayoutPercent,
    });
    return Customer.parse(response.data);
  }

  async createCustomer(customer: CustomerCreateSchemaType): Promise<unknown> {
    return await this._client.post('/customers', {
      name: customer.name,
      address: customer.address,
      email: customer.email,
      phone: customer.phoneNumber,
      payout_mode: customer.payout_mode,
      enforcement_payout_mode: customer.enforcement_payout_mode,
      timezone: customer.timezone,
      net_invoice_payout_percent: customer.netInvoicePayoutPercent,
    });
  }

  async processPayouts(customerId: string) {
    return await this._client.post('/stripe/payouts/process', {
      customer_id: customerId,
    });
  }

  async addBankAccount(customerId: string, bankAccountDisplayName: string) {
    return await this._client.post('/bank_accounts', {
      customer_id: customerId,
      name: bankAccountDisplayName,
    });
  }

  async createGenerateReportRequest(req: GenerateReportRequest) {
    // Heavy report queries can take several minutes to accept; override default 60s.
    return (
      await this._client.post('/reports/generate', req, {
        timeout: 900_000,
      })
    ).data as GenerateReportResponse;
  }

  async getReportSchedules(): Promise<ReportScheduleListType> {
    const response = await this._client.get('/reports/schedules');
    return ReportScheduleList.parse(response.data);
  }

  async createReportSchedule(
    request: CreateReportScheduleRequestType
  ): Promise<ReportScheduleType> {
    const response = await this._client.post('/reports/schedules', request);
    return ReportSchedule.parse(response.data);
  }

  async updateReportSchedule(
    id: string,
    request: UpdateReportScheduleRequestType
  ): Promise<ReportScheduleType> {
    const response = await this._client.put(
      `/reports/schedules/${id}`,
      request
    );
    return ReportSchedule.parse(response.data);
  }

  async deleteReportSchedule(id: string): Promise<void> {
    await this._client.delete(`/reports/schedules/${id}`);
  }

  async getReservationById(reservationId: string) {
    const response = await this._client.get(`/reservation/id/${reservationId}`);
    return Reservation.parse(response.data);
  }

  async updateCurrentUser(payload: UpdateUserPayload): Promise<void> {
    await this._client.patch('/auth/me/user', payload);
  }

  async getCurrentUserAuthLogs() {
    const response = await this._client.get('/auth/me/logs');
    return response.data as LogData;
  }

  async getOrgMembers(options?: AuthResourcesPaginationOptions) {
    const response = await this._client.get('/user-management/users', {
      params: options,
    });
    return response.data as OrganizationMemberList;
  }

  async deleteUser(userId: string) {
    const response = await this._client.delete(
      `/user-management/users/${userId}`
    );
    return response.data;
  }

  async getAllRoles() {
    const response = await this._client.get('/user-management/roles');
    return response.data as RoleList;
  }

  async getUser(userId: string) {
    const response = await this._client.get(`/user-management/users/${userId}`);
    return UserSchema.parse(response.data);
  }

  async assignRolesToOrgMember(userId: string, roles: string[]) {
    const response = await this._client.post(
      `/user-management/users/${userId}/roles`,
      roles
    );
    return response.data;
  }

  async deleteRolesFromOrgMember(userId: string, roles: string[]) {
    const response = await this._client.delete(
      `/user-management/users/${userId}/roles`,
      {
        data: roles,
      }
    );
    return response.data;
  }

  async updateUserPayload(userId: string, data: UserOrgMemberPayload) {
    const response = await this._client.patch(
      `/user-management/users/${userId}`,
      data
    );
    return response.data;
  }

  async getInvitations(options?: AuthResourcesPaginationOptions) {
    const response = await this._client.get('/user-management/invitations', {
      params: options,
    });
    return response.data as OrganizationInvitationList;
  }

  async inviteUser(data: InviteUserPayload) {
    const response = await this._client.post(
      `/user-management/invitations`,
      data
    );
    return response.data;
  }

  async deleteInvitation(userId: string) {
    const response = await this._client.delete(
      `/user-management/invitations/${userId}`
    );
    return response.data;
  }

  async getParkingSessions({
    locationIds,
    limit,
    id,
    ascending = false,
    status,
    plate,
    signal,
    plateState,
    startDateGTE,
    startDateLTE,
    coversAt,
    fuzzySearchByPlate = false,
    source,
    withinLocationGroup,
    hasDispute,
  }: {
    locationIds: string[];
    limit: number;
    id?: string;
    ascending?: boolean;
    status?: string | null;
    plate?: string | null;
    plateState?: PlateStateAbbreviation | null;
    startDateLTE?: DateTime<true> | null;
    startDateGTE?: DateTime<true> | null;
    coversAt?: DateTime<true> | null;
    signal?: AbortSignal;
    fuzzySearchByPlate?: boolean;
    source?: 'user' | 'passive' | null;
    withinLocationGroup?: boolean;
    hasDispute?: boolean | null;
  }) {
    const response = await this._client.get(`/sessions/`, {
      params: removeNullValuesFromObject({
        location_ids: locationIds,
        limit,
        id: id,
        ascending,
        status,
        plate,
        plate_state: plateState,
        start_date_gte: startDateGTE?.toISO(),
        start_date_lte: startDateLTE?.toISO(),
        covers_at: coversAt?.toISO(),
        fuzzy_search_by_plate: fuzzySearchByPlate,
        source,
        within_location_group: withinLocationGroup || undefined,
        has_dispute: hasDispute,
      }),
      signal,
    });

    return ParkingSessionsPaginatedResult.parse(response.data);
  }

  async getParkingSessionById(sessionId: string) {
    const response = await this._client.get(`/sessions/private/${sessionId}`);
    return PrivateSessionResponseSchema.parse(response.data);
  }

  async correctSessionPlate(
    sessionId: string,
    data: CorrectPlateRequest
  ): Promise<CorrectPlateResponse> {
    const response = await this._client.post(
      `/sessions/${sessionId}/correct-plate`,
      data
    );
    return CorrectPlateResponseSchema.parse(response.data);
  }

  async clearSessionPhone(
    sessionId: string,
    data: ClearSessionPhoneRequest = {}
  ): Promise<ClearSessionPhoneResponse> {
    const response = await this._client.post(
      `/sessions/${sessionId}/clear-phone`,
      data
    );
    return ClearSessionPhoneResponseSchema.parse(response.data);
  }

  async getActiveSessionCheck({
    plate,
    locationId,
    signal,
  }: {
    plate: string;
    locationId: string;
    signal?: AbortSignal;
  }): Promise<ActiveSessionCheckResponse> {
    const response = await this._client.get('/monitor/sessions/active', {
      params: {
        plate_number: plate,
        location_id: locationId,
      },
      signal,
    });
    return ActiveSessionCheckResponseSchema.parse(response.data);
  }

  async getGuestToken(): Promise<string> {
    const response = await this._client.post('/superset/guest_token');
    return response.data;
  }

  async getParkingMetrics({
    startDate,
    endDate,
    locationIds,
    timezone,
  }: {
    startDate: DateTime<true>;
    endDate: DateTime<true>;
    locationIds: string[];
    timezone: string;
  }): Promise<ParkingMetricsType> {
    const response = await this._client.get('/dashboard/parking/metrics', {
      params: removeNullValuesFromObject({
        start_date: startDate.toISO(),
        end_date: endDate.toISO(),
        location_ids: locationIds.join(','),
        timezone,
      }),
    });
    const parsed = ParkingMetricsSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        '[getParkingMetrics] Response shape mismatch:',
        parsed.error.flatten()
      );
      throw new Error('Parking metrics validation failed.');
    }
    return parsed.data;
  }

  async getEnforcementMetrics({
    startDate,
    endDate,
    locationIds,
    timezone,
  }: {
    startDate: DateTime<true>;
    endDate: DateTime<true>;
    locationIds: string[];
    timezone: string;
  }): Promise<EnforcementMetricsType> {
    const response = await this._client.get('/dashboard/enforcement/metrics', {
      params: removeNullValuesFromObject({
        start_date: startDate.toISO(),
        end_date: endDate.toISO(),
        location_ids: locationIds.join(','),
        timezone,
      }),
    });
    const parsed = EnforcementMetricsSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        '[getEnforcementMetrics] Response shape mismatch:',
        parsed.error.flatten()
      );
      throw new Error('Enforcement metrics validation failed.');
    }
    return parsed.data;
  }

  // attributionWindowHours overrides how long after a click a payment still
  // counts as drip-driven. Omitted, the server applies its own default (72h,
  // matching the drip cadence) and reports it back on the response.
  async getDripFunnelMetrics({
    startDate,
    endDate,
    locationIds,
    timezone,
    attributionWindowHours,
  }: {
    startDate: DateTime<true>;
    endDate: DateTime<true>;
    locationIds: string[];
    timezone: string;
    attributionWindowHours?: number;
  }): Promise<DripFunnelMetricsType> {
    const response = await this._client.get(
      '/dashboard/enforcement/drip-funnel',
      {
        params: removeNullValuesFromObject({
          start_date: startDate.toISO(),
          end_date: endDate.toISO(),
          location_ids: locationIds.join(','),
          timezone,
          attribution_window_hours: attributionWindowHours ?? null,
        }),
      }
    );
    const parsed = DripFunnelMetricsSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        '[getDripFunnelMetrics] Response shape mismatch:',
        parsed.error.flatten()
      );
      throw new Error('Drip funnel metrics validation failed.');
    }
    return parsed.data;
  }

  async getMonthlyParkerMetrics({
    startDate,
    endDate,
    locationIds,
    timezone,
  }: {
    startDate: DateTime<true>;
    endDate: DateTime<true>;
    locationIds: string[];
    timezone: string;
  }): Promise<MonthlyParkerMetricsType> {
    const response = await this._client.get('/dashboard/monthly/metrics', {
      params: removeNullValuesFromObject({
        start_date: startDate.toISO(),
        end_date: endDate.toISO(),
        location_ids: locationIds.join(','),
        timezone,
      }),
    });
    const parsed = MonthlyParkerMetricsSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        '[getMonthlyParkerMetrics] Response shape mismatch:',
        parsed.error.flatten()
      );
      throw new Error('Monthly parker metrics validation failed.');
    }
    return parsed.data;
  }

  // Device Management (Gate Controllers)

  async getDevices(locationId: string): Promise<DeviceListType> {
    const response = await this._client.get('/devices', {
      params: {
        locationId,
      },
    });
    return DeviceList.parse(response.data);
  }

  async getDevice(deviceId: string): Promise<DeviceType> {
    const response = await this._client.get(`/devices/${deviceId}`);
    return Device.parse(response.data);
  }

  async createDevice(request: CreateDeviceRequestType): Promise<DeviceType> {
    const response = await this._client.post('/devices', request);
    return Device.parse(response.data);
  }

  async updateDevice(
    deviceId: string,
    request: UpdateDeviceRequestType
  ): Promise<DeviceType> {
    const response = await this._client.put(`/devices/${deviceId}`, request);
    return Device.parse(response.data);
  }

  async deleteDevice(deviceId: string): Promise<void> {
    await this._client.delete(`/devices/${deviceId}`);
  }

  async sendDeviceCommand(
    deviceId: string,
    command: DeviceCommandType
  ): Promise<DeviceCommandResultType> {
    const response = await this._client.post(
      `/devices/${deviceId}/command`,
      command
    );
    return DeviceCommandResult.parse(response.data);
  }

  async getAccessDecisions(
    locationId: string,
    options?: {
      deviceId?: string;
      plate?: string;
      startTime?: string;
      endTime?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<AccessDecisionsResponseType> {
    const response = await this._client.get(
      `/locations/${locationId}/access-decisions`,
      {
        params: removeNullValuesFromObject({
          device_id: options?.deviceId,
          plate: options?.plate,
          start_time: options?.startTime,
          end_time: options?.endTime,
          limit: options?.limit,
          offset: options?.offset,
        }),
      }
    );
    return AccessDecisionsResponse.parse(response.data);
  }

  // Whitelist Management

  async getWhitelistEntries(
    deviceId: string
  ): Promise<WhitelistEntriesResponseType> {
    const response = await this._client.get(
      `/devices/${deviceId}/whitelist/entries`
    );
    return WhitelistEntriesResponse.parse(response.data);
  }

  async getWhitelistStatus(deviceId: string): Promise<WhitelistStatusType> {
    const response = await this._client.get(
      `/devices/${deviceId}/whitelist/status`
    );
    return WhitelistStatus.parse(response.data);
  }

  async triggerWhitelistSync(
    deviceId: string
  ): Promise<{ message: string; sync_started: boolean }> {
    const response = await this._client.post(
      `/devices/${deviceId}/whitelist/sync`
    );
    return response.data;
  }

  async getAccessDecisionCameraCapture(
    deviceId: string,
    decisionId: string
  ): Promise<CameraCaptureResponseType | null> {
    try {
      const response = await this._client.get(
        `/devices/${deviceId}/access-decisions/${decisionId}/camera-capture`
      );
      return CameraCaptureResponse.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Access Control Schedule Management

  async getLocationSchedule(
    locationId: string
  ): Promise<AccessControlScheduleType | null> {
    try {
      const response = await this._client.get(
        `/locations/${locationId}/access-control/schedule`
      );
      // API returns 200 with { message: "..." } when no schedule configured.
      if (response.data?.message && !response.data?.id) {
        return null;
      }
      return AccessControlSchedule.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createOrUpdateLocationSchedule(
    locationId: string,
    request: CreateScheduleRequestType
  ): Promise<AccessControlScheduleType> {
    const response = await this._client.put(
      `/locations/${locationId}/access-control/schedule`,
      request
    );
    return AccessControlSchedule.parse(response.data);
  }

  async addSchedulePeriod(
    locationId: string,
    request: CreatePeriodRequestType
  ): Promise<AccessControlPeriodType> {
    const response = await this._client.post(
      `/locations/${locationId}/access-control/schedule/periods`,
      request
    );
    return AccessControlPeriod.parse(response.data);
  }

  async deleteSchedulePeriod(
    locationId: string,
    periodId: string
  ): Promise<void> {
    await this._client.delete(
      `/locations/${locationId}/access-control/schedule/periods/${periodId}`
    );
  }

  async addScheduleOverride(
    locationId: string,
    request: CreateOverrideRequestType
  ): Promise<AccessControlOverrideType> {
    const response = await this._client.post(
      `/locations/${locationId}/access-control/schedule/overrides`,
      request
    );
    return AccessControlOverride.parse(response.data);
  }

  async updateScheduleOverride(
    locationId: string,
    overrideId: string,
    request: UpdateOverrideRequestType
  ): Promise<AccessControlOverrideType> {
    const response = await this._client.put(
      `/locations/${locationId}/access-control/schedule/overrides/${overrideId}`,
      request
    );
    return AccessControlOverride.parse(response.data);
  }

  async deleteScheduleOverride(
    locationId: string,
    overrideId: string
  ): Promise<void> {
    await this._client.delete(
      `/locations/${locationId}/access-control/schedule/overrides/${overrideId}`
    );
  }

  async getScheduleTemplates(): Promise<ScheduleTemplatesResponseType> {
    const response = await this._client.get(
      '/access-control/schedule-templates'
    );
    return ScheduleTemplatesResponse.parse(response.data);
  }

  // Access Point Schedule Overrides

  async getAccessPointSchedule(
    accessPointId: string
  ): Promise<AccessPointScheduleType> {
    const response = await this._client.get(
      `/access-points/${accessPointId}/schedule`
    );
    return AccessPointSchedule.parse(response.data);
  }

  async setAccessPointSchedule(
    accessPointId: string,
    request: SetAccessPointScheduleRequestType
  ): Promise<AccessPointScheduleType> {
    const response = await this._client.put(
      `/access-points/${accessPointId}/schedule`,
      request
    );
    return AccessPointSchedule.parse(response.data);
  }

  async getAccessPointsForLocation(
    locationId: string
  ): Promise<AccessPointListType> {
    const response = await this._client.get(
      `/locations/${locationId}/access-points`
    );
    return AccessPointList.parse(response.data);
  }

  async openAccessPoint(
    accessPointId: string
  ): Promise<AccessPointCommandResultType> {
    const response = await this._client.post(
      `/access-points/${accessPointId}/open`
    );
    return AccessPointCommandResult.parse(response.data);
  }

  async getAccessPointCameras(
    accessPointId: string
  ): Promise<AssociatedCameraListType> {
    const response = await this._client.get(
      `/access-points/${accessPointId}/cameras`
    );
    return AssociatedCameraList.parse(response.data);
  }

  async setAccessPointCameras(
    accessPointId: string,
    cameraIds: string[]
  ): Promise<AssociatedCameraListType> {
    const response = await this._client.put(
      `/access-points/${accessPointId}/cameras`,
      { camera_ids: cameraIds }
    );
    return AssociatedCameraList.parse(response.data);
  }

  async getVehicleMakesWithModels(): Promise<VehicleMakeWithModelsList> {
    const response = await this._client.get('/vehicle/v2/makes-with-models');
    return VehicleMakeWithModelsListSchema.parse(response.data);
  }

  /**
   * Get a validation by its ID
   * Returns null if not found (404)
   */
  async getValidationById(
    validationId: string
  ): Promise<ValidationType | null> {
    try {
      const response = await this._client.get(`/validation/id/${validationId}`);
      return ValidationSchema.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Terminate an active usage-based validation.
   * API returns 204 No Content on success.
   */
  async terminateValidation(validationId: string): Promise<void> {
    await this._client.post(`/validation/id/${validationId}/terminate`);
  }

  // Plan Management

  async getPlans(locationId: string): Promise<PlanListType> {
    const response = await this._client.get('/monthly-parking/plans', {
      params: { location_id: locationId },
    });
    return PlanListSchema.parse(response.data);
  }

  async getPlanById(_locationId: string, planId: string): Promise<PlanType> {
    const response = await this._client.get(`/monthly-parking/plans/${planId}`);
    return PlanSchema.parse(response.data);
  }

  async createPlan(
    locationId: string,
    data: CreatePlanSchemaType
  ): Promise<PlanType> {
    const response = await this._client.post('/monthly-parking/plans', {
      ...data,
      location_id: locationId,
    });
    return PlanSchema.parse(response.data);
  }

  async updatePlan(
    _locationId: string,
    planId: string,
    data: UpdatePlanSchemaType
  ): Promise<PlanType> {
    const response = await this._client.patch(
      `/monthly-parking/plans/${planId}`,
      data
    );
    return PlanSchema.parse(response.data);
  }

  async archivePlan(_locationId: string, planId: string): Promise<PlanType> {
    const response = await this._client.patch(
      `/monthly-parking/plans/${planId}`,
      { status: 'archived' }
    );
    return PlanSchema.parse(response.data);
  }

  async getPlanVersions(
    _locationId: string,
    planId: string
  ): Promise<PlanVersionType[]> {
    const response = await this._client.get(
      `/monthly-parking/plans/${planId}/versions`
    );
    return z.array(PlanVersionSchema).parse(response.data);
  }

  async createPlanVersion(
    _locationId: string,
    planId: string,
    data: CreatePlanVersionSchemaType
  ): Promise<PlanVersionType> {
    const response = await this._client.post(
      `/monthly-parking/plans/${planId}/versions`,
      data
    );
    return PlanVersionSchema.parse(response.data);
  }

  async togglePlanVersionEffective(
    _locationId: string,
    planId: string,
    versionId: string,
    effectiveForNewSignups: boolean
  ): Promise<void> {
    await this._client.patch(
      `/monthly-parking/plans/${planId}/versions/${versionId}`,
      { effective_for_new_signups: effectiveForNewSignups }
    );
  }

  async clonePlanVersion(
    _locationId: string,
    planId: string,
    versionId: string,
    data: ClonePlanVersionSchemaType
  ): Promise<PlanVersionType> {
    const response = await this._client.post(
      `/monthly-parking/plans/${planId}/versions/${versionId}/clone`,
      data
    );
    return PlanVersionSchema.parse(response.data);
  }

  async rateChangePlanVersion(
    _locationId: string,
    planId: string,
    versionId: string,
    data: RateChangeSchemaType
  ): Promise<RateChangeResponseType> {
    const response = await this._client.post(
      `/monthly-parking/plans/${planId}/versions/${versionId}/rate-change`,
      data
    );
    return RateChangeResponseSchema.parse(response.data);
  }

  // Group Agreements Management

  async getGroups(params: {
    locationId: string;
    limit: number;
    ascending?: boolean;
    locationIds?: string[];
    startKey?: GroupAgreementPaginationKey;
    signal?: AbortSignal;
  }): Promise<GroupAgreementPaginatedResultType> {
    const { limit, ascending, locationIds, startKey, signal } = params;
    const response = await this._client.get('/monthly-parking/groups', {
      params: removeNullValuesFromObject({
        limit,
        ascending,
        location_ids: locationIds,
        created_at: startKey?.created_at,
        id: startKey?.id,
      }),
      signal,
    });
    return GroupAgreementPaginatedResult.parse(response.data);
  }

  async getGroup(groupId: string): Promise<GroupAgreementDetailType> {
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}`
    );
    return GroupAgreementDetailSchema.parse(response.data);
  }

  async getGroupInvoices(params: {
    groupId: string;
    limit: number;
    startKey?: InvoicePaginationKey;
    signal?: AbortSignal;
  }): Promise<GroupInvoicesPaginatedResultType> {
    const { groupId, limit, startKey, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/invoices`,
      {
        params: removeNullValuesFromObject({
          limit,
          startKey,
        }),
        signal,
      }
    );
    return GroupInvoicesPaginatedResult.parse(response.data);
  }

  async getTierSeats(params: {
    groupId: string;
    tierId: string;
    limit: number;
    ascending?: boolean;
    q?: string;
    startKey?: SeatPaginationKey;
    signal?: AbortSignal;
  }): Promise<TierSeatsPaginatedResultType> {
    const { groupId, tierId, limit, ascending, q, startKey, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats`,
      {
        params: removeNullValuesFromObject({
          limit,
          ascending,
          q,
          created_at: startKey?.created_at,
          id: startKey?.id,
        }),
        signal,
      }
    );
    return TierSeatsPaginatedResult.parse(response.data);
  }

  async getGroupUpcomingInvoice(groupId: string): Promise<GroupInvoiceType> {
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/invoices/upcoming`
    );
    return GroupInvoiceSchema.parse(response.data);
  }

  // Without from/to the server reports the location's current month to date.
  async getGroupOverageUsage(params: {
    groupId: string;
    from?: string;
    to?: string;
    signal?: AbortSignal;
  }): Promise<GroupOverageUsageType> {
    const { groupId, from, to, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/overage-days`,
      { params: removeNullValuesFromObject({ from, to }), signal }
    );
    return GroupOverageUsageSchema.parse(response.data);
  }

  async getGroupOverageCountedVehicles(params: {
    groupId: string;
    from: string;
    to: string;
    signal?: AbortSignal;
  }): Promise<GroupOverageCountedVehiclesType> {
    const { groupId, from, to, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/overage-days/plates`,
      { params: { from, to }, signal }
    );
    return GroupOverageCountedVehiclesSchema.parse(response.data);
  }

  async createGroup(
    locationId: string,
    data: CreateGroupRequestSchemaType
  ): Promise<GroupAgreementType> {
    const response = await this._client.post('/monthly-parking/groups', {
      ...data,
      location_id: locationId,
    });
    return GroupAgreementSchema.parse(response.data);
  }

  async addGroupAdmin(
    groupId: string,
    data: {
      email: string;
      full_name?: string | null;
      phone_number?: string | null;
    }
  ): Promise<void> {
    await this._client.post(`/monthly-parking/groups/${groupId}/admins`, data);
  }

  async inviteTierMember(
    groupId: string,
    tierId: string,
    data: {
      email?: string | null;
      first_name?: string | null;
      last_name?: string | null;
      phone_number?: string | null;
      skip_invitation?: boolean;
      preferred_reserved_space_id?: string;
      vehicles?: {
        plate: string;
        plate_state: string;
        make_id?: number;
        model_id?: number;
        color?: string;
      }[];
    }
  ): Promise<void> {
    await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/invitations`,
      data
    );
  }

  async resendAdminInvitation(groupId: string, adminId: string): Promise<void> {
    await this._client.post(
      `/monthly-parking/groups/${groupId}/admins/${adminId}/resend-invitation`
    );
  }

  async resendSeatInvitation(
    groupId: string,
    tierId: string,
    seatId: string
  ): Promise<void> {
    await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/resend-invitation`
    );
  }

  async removeTierMember(
    groupId: string,
    tierId: string,
    seatId: string
  ): Promise<void> {
    await this._client.delete(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}`
    );
  }

  async cancelReservedUpgrade(
    groupId: string,
    tierId: string,
    seatId: string
  ): Promise<void> {
    await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/cancel-reserved-upgrade`
    );
  }

  async updateSeatPreferredSpace(
    groupId: string,
    tierId: string,
    seatId: string,
    data: { preferred_reserved_space_id: string }
  ): Promise<SeatResponseType> {
    const response = await this._client.patch(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/preferred-space`,
      data
    );
    return SeatResponseSchema.parse(response.data);
  }

  async exchangeTierReservedSpace(
    groupId: string,
    tierId: string,
    data: ExchangeTierReservedSpaceRequestType
  ): Promise<SubagreementType> {
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/reserved-spaces/exchange`,
      data
    );
    return SubagreementSchema.parse(response.data);
  }

  async moveSeatToTier(
    groupId: string,
    tierId: string,
    seatId: string,
    data: {
      target_subagreement_id: string;
      preferred_reserved_space_id?: string | null;
    }
  ): Promise<SeatResponseType> {
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/move`,
      data
    );
    return SeatResponseSchema.parse(response.data);
  }

  async swapSeats(
    groupId: string,
    data: { seat_a_id: string; seat_b_id: string }
  ): Promise<SwapSeatsResponseType> {
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/seats/swap`,
      data
    );
    return SwapSeatsResponseSchema.parse(response.data);
  }

  async addSeatVehicle(
    groupId: string,
    tierId: string,
    seatId: string,
    data: AddSeatVehicleRequestType
  ): Promise<SeatVehicleType> {
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/vehicles`,
      data
    );
    return SeatVehicleSchema.parse(response.data);
  }

  async updateSeatVehicle(
    groupId: string,
    tierId: string,
    seatId: string,
    vehicleId: string,
    data: UpdateSeatVehicleRequestType
  ): Promise<SeatVehicleType> {
    const response = await this._client.patch(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/vehicles/${vehicleId}`,
      data
    );
    return SeatVehicleSchema.parse(response.data);
  }

  async removeSeatVehicle(
    groupId: string,
    tierId: string,
    seatId: string,
    vehicleId: string
  ): Promise<void> {
    await this._client.delete(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}/seats/${seatId}/vehicles/${vehicleId}`
    );
  }

  async removeGroupAdmin(groupId: string, adminId: string): Promise<void> {
    await this._client.delete(
      `/monthly-parking/groups/${groupId}/admins/${adminId}`
    );
  }

  async cancelGroup(groupId: string): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      status: 'canceled',
    });
  }

  async reactivateGroup(groupId: string): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      status: 'reactivated',
    });
  }

  async terminateGroup(groupId: string): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      status: 'terminated',
    });
  }

  // Pass null for value_cents to clear the override and fall back to the
  // location's platform fee.
  async updateGroupPlatformFeeOverride(
    groupId: string,
    valueCents: number | null
  ): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      platform_fee_override: { value_cents: valueCents },
    });
  }

  // Pass null for html to clear the terms override and re-inherit the
  // location-wide terms. A non-null string sets or replaces the override; the
  // server sanitizes the HTML before storing it.
  async updateGroupTermsOverride(
    groupId: string,
    html: string | null
  ): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      terms_override: { html },
    });
  }

  async updateGroupOverageSettings(
    groupId: string,
    settings: UpdateGroupOverageSettingsRequest
  ): Promise<void> {
    await this._client.patch(`/monthly-parking/groups/${groupId}`, {
      overage_settings: settings,
    });
  }

  // Re-points an admin_pays agreement to a different accepted admin as the
  // billing payer. newPayerAdminId must be an accepted admin of this agreement
  // with a linked account, and not already the current payer.
  async changeGroupPayer(
    groupId: string,
    newPayerAdminId: string
  ): Promise<ChangeGroupPayerResponseType> {
    const response = await this._client.patch(
      `/monthly-parking/groups/${groupId}`,
      { payer_change: { new_payer_admin_id: newPayerAdminId } }
    );
    return ChangeGroupPayerResponseSchema.parse(response.data);
  }

  // Switches an admin_pays agreement between charge_automatically and
  // send_invoice. The `collection_method` branch is flat: the method and its
  // send_invoice config fields are top-level siblings (unlike payer_change),
  // so payload is sent as the request body verbatim. Its shape is constrained
  // by the discriminated union, so send_invoice config only rides along when
  // switching to invoicing. The reconcile against Stripe is idempotent, so a
  // 502 is safe to retry.
  async updateGroupCollectionMethod(
    groupId: string,
    payload: ChangeCollectionMethodRequestType
  ): Promise<ChangeCollectionMethodResponseType> {
    const response = await this._client.patch(
      `/monthly-parking/groups/${groupId}`,
      payload
    );
    return ChangeCollectionMethodResponseSchema.parse(response.data);
  }

  async sendGroupInvoicePreview(
    groupId: string,
    options?: { force?: boolean; dryRun?: boolean }
  ): Promise<SendInvoicePreviewResponseType> {
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/invoices/preview/send`,
      undefined,
      {
        params: removeNullValuesFromObject({
          force: options?.force ? true : undefined,
          dry_run: options?.dryRun ? true : undefined,
        }),
      }
    );
    return SendInvoicePreviewResponseSchema.parse(response.data);
  }

  async updateTier(
    groupId: string,
    tierId: string,
    data: {
      lease_included_seats?: number;
      lease_allocation_id?: string | null;
    }
  ): Promise<void> {
    await this._client.patch(
      `/monthly-parking/group-agreements/${groupId}/subagreements/${tierId}`,
      data
    );
  }

  // Downgrade only: new_seat_count must be below the tier's current
  // seat_count. Mixing with grow fields is rejected by the backend.
  async reduceTierSeats(
    groupId: string,
    tierId: string,
    data: ReduceTierSeatsRequestType
  ): Promise<SubagreementType> {
    const response = await this._client.put(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}`,
      data
    );
    return SubagreementSchema.parse(response.data);
  }

  // Grow branch of the same PUT. Mixing with downgrade or draft fields is
  // rejected by the backend.
  async growTierSeats(
    groupId: string,
    tierId: string,
    data: GrowTierSeatsRequestType
  ): Promise<SubagreementType> {
    const response = await this._client.put(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}`,
      data
    );
    return SubagreementSchema.parse(response.data);
  }

  // Policy branch of the same PUT. Mixing with grow or downgrade fields is
  // rejected by the backend.
  async updateTierCapacityPolicy(
    groupId: string,
    tierId: string,
    data: UpdateTierCapacityPolicyRequestType
  ): Promise<SubagreementType> {
    const response = await this._client.put(
      `/monthly-parking/groups/${groupId}/tiers/${tierId}`,
      data
    );
    return SubagreementSchema.parse(response.data);
  }

  // Group Agreement Documents

  async createGroupDocumentUpload(input: {
    locationId: string;
    type: GroupDocumentType;
    file: File;
    signal?: AbortSignal;
  }): Promise<GroupDocumentUploadType> {
    const formData = new FormData();
    formData.append('location_id', input.locationId);
    formData.append('type', input.type);
    formData.append('file', input.file);
    const response = await this._client.post(
      '/monthly-parking/group-document-uploads',
      formData,
      { signal: input.signal }
    );
    return GroupDocumentUploadSchema.parse(response.data);
  }

  async getGroupDocuments(
    groupId: string
  ): Promise<GroupAgreementDocumentType[]> {
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/documents`
    );
    return GroupAgreementDocumentsResponseSchema.parse(response.data).documents;
  }

  async getGroupDocument(
    groupId: string,
    docId: string
  ): Promise<GroupAgreementDocumentType> {
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/documents/${docId}`
    );
    return GroupAgreementDocumentResponseSchema.parse(response.data).document;
  }

  async createGroupDocument(
    groupId: string,
    input: { type: GroupDocumentType; file: File }
  ): Promise<GroupAgreementDocumentType> {
    const formData = new FormData();
    formData.append('type', input.type);
    formData.append('file', input.file);
    const response = await this._client.post(
      `/monthly-parking/groups/${groupId}/documents`,
      formData
    );
    return GroupAgreementDocumentSchema.parse(response.data);
  }

  async updateGroupDocumentType(
    groupId: string,
    docId: string,
    type: GroupDocumentType
  ): Promise<GroupAgreementDocumentType> {
    const response = await this._client.patch(
      `/monthly-parking/groups/${groupId}/documents/${docId}`,
      { type }
    );
    return GroupAgreementDocumentSchema.parse(response.data);
  }

  async replaceGroupDocumentFile(
    groupId: string,
    docId: string,
    file: File
  ): Promise<GroupAgreementDocumentType> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this._client.put(
      `/monthly-parking/groups/${groupId}/documents/${docId}/file`,
      formData
    );
    return GroupAgreementDocumentSchema.parse(response.data);
  }

  async deleteGroupDocument(groupId: string, docId: string): Promise<void> {
    await this._client.delete(
      `/monthly-parking/groups/${groupId}/documents/${docId}`
    );
  }

  // Lease Allocations

  async getLeaseAllocations(
    groupAgreementId: string
  ): Promise<LeaseAllocationWithTiersType[]> {
    const response = await this._client.get(
      `/monthly-parking/group-agreements/${groupAgreementId}/lease-allocations`
    );
    return LeaseAllocationListSchema.parse(response.data);
  }

  async createLeaseAllocation(
    groupAgreementId: string,
    data: CreateLeaseAllocationRequestType
  ): Promise<LeaseAllocationType> {
    const response = await this._client.post(
      `/monthly-parking/group-agreements/${groupAgreementId}/lease-allocations`,
      data
    );
    return LeaseAllocationSchema.parse(response.data);
  }

  async updateLeaseAllocation(
    groupAgreementId: string,
    allocationId: string,
    data: UpdateLeaseAllocationRequestType
  ): Promise<LeaseAllocationType> {
    const response = await this._client.patch(
      `/monthly-parking/group-agreements/${groupAgreementId}/lease-allocations/${allocationId}`,
      data
    );
    return LeaseAllocationSchema.parse(response.data);
  }

  async deleteLeaseAllocation(
    groupAgreementId: string,
    allocationId: string
  ): Promise<void> {
    await this._client.delete(
      `/monthly-parking/group-agreements/${groupAgreementId}/lease-allocations/${allocationId}`
    );
  }

  async convertSeatToLease(seatId: string): Promise<SeatType> {
    const response = await this._client.post(
      `/monthly-parking/group-seats/${seatId}/convert-to-lease`
    );
    return SeatSchema.parse(response.data);
  }

  async convertSeatToBilled(seatId: string): Promise<SeatType> {
    const response = await this._client.post(
      `/monthly-parking/group-seats/${seatId}/convert-to-billed`
    );
    return SeatSchema.parse(response.data);
  }

  async resetVehicleChangeCounter(
    seatId: string,
    reason?: string
  ): Promise<void> {
    await this._client.post(
      `/monthly-parking/group-seats/${seatId}/reset-vehicle-change-counter`,
      reason ? { reason } : {}
    );
  }

  async getGroupAuditTrail(params: {
    groupId: string;
    limit: number;
    ascending?: boolean;
    startKey?: AuditTrailPaginationKey;
    signal?: AbortSignal;
  }): Promise<GroupAuditTrailPaginatedResultType> {
    const { groupId, limit, ascending, startKey, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/groups/${groupId}/audit-trail`,
      {
        params: removeNullValuesFromObject({
          limit,
          ascending,
          created_at: startKey?.created_at,
          id: startKey?.id,
        }),
        signal,
      }
    );
    return GroupAuditTrailPaginatedResult.parse(response.data);
  }

  // Individual Agreements (Billing Agreements)

  async getAgreements(params: {
    locationId: string;
    limit: number;
    ascending?: boolean;
    locationIds?: string[];
    status?: string;
    startKey?: AgreementPaginationKey;
    signal?: AbortSignal;
  }): Promise<AgreementPaginatedResultType> {
    const { limit, ascending, locationIds, status, startKey, signal } = params;
    const response = await this._client.get('/monthly-parking/agreements', {
      params: removeNullValuesFromObject({
        limit,
        ascending,
        location_ids: locationIds,
        status,
        created_at: startKey?.created_at,
        id: startKey?.id,
      }),
      signal,
    });
    return AgreementPaginatedResult.parse(response.data);
  }

  async getAgreement(agreementId: string): Promise<AgreementDetailType> {
    const response = await this._client.get(
      `/monthly-parking/agreements/${agreementId}`
    );
    return AgreementDetailSchema.parse(response.data);
  }

  async resetAgreementVehicleChangeCounter(
    agreementId: string,
    reason?: string
  ): Promise<void> {
    await this._client.post(
      `/monthly-parking/agreements/${agreementId}/reset-vehicle-change-counter`,
      reason ? { reason } : {}
    );
  }

  async getAgreementInvoices(params: {
    agreementId: string;
    limit: number;
    startKey?: InvoicePaginationKey;
    signal?: AbortSignal;
  }): Promise<AgreementInvoicesPaginatedResultType> {
    const { agreementId, limit, startKey, signal } = params;
    const response = await this._client.get(
      `/monthly-parking/agreements/${agreementId}/invoices`,
      {
        params: removeNullValuesFromObject({
          limit,
          startKey,
        }),
        signal,
      }
    );
    return AgreementInvoicesPaginatedResult.parse(response.data);
  }

  async getAgreementUpcomingInvoice(
    agreementId: string
  ): Promise<AgreementInvoiceType> {
    const response = await this._client.get(
      `/monthly-parking/agreements/${agreementId}/invoices/upcoming`
    );
    return AgreementInvoiceSchema.parse(response.data);
  }

  async getCapacityStats(locationId: string): Promise<CapacityStatsType> {
    const response = await this._client.get(
      `/locations/${locationId}/capacity`
    );
    return CapacityStatsSchema.parse(response.data);
  }

  async getCapacityCommitments(
    locationId: string
  ): Promise<CapacityCommitmentsType> {
    const response = await this._client.get(
      `/monthly-parking/locations/${locationId}/capacity/commitments`
    );
    return CapacityCommitmentsSchema.parse(response.data);
  }

  async getReservedSpaces(locationId: string): Promise<ReservedSpaceListType> {
    const response = await this._client.get(
      `/monthly-parking/locations/${locationId}/reserved-spaces`
    );
    return ReservedSpaceListSchema.parse(response.data);
  }

  async getReservedSpace(
    locationId: string,
    spaceId: string
  ): Promise<ReservedSpaceType> {
    const response = await this._client.get(
      `/monthly-parking/locations/${locationId}/reserved-spaces/${spaceId}`
    );
    return ReservedSpaceSchema.parse(response.data);
  }

  async createReservedSpaces(
    locationId: string,
    data: CreateReservedSpacesRequestType
  ): Promise<ReservedSpaceListType> {
    const response = await this._client.post(
      `/monthly-parking/locations/${locationId}/reserved-spaces`,
      data
    );
    return ReservedSpaceListSchema.parse(response.data);
  }

  async deleteReservedSpace(
    locationId: string,
    spaceId: string
  ): Promise<void> {
    await this._client.delete(
      `/monthly-parking/locations/${locationId}/reserved-spaces/${spaceId}`
    );
  }

  // Camera Management

  async getCameras(): Promise<CameraListType> {
    const response = await this._client.get('/monitor/cameras');
    return CameraListSchema.parse(response.data);
  }

  async getCameraMetrics(timezone: string): Promise<CameraMetricsType> {
    const response = await this._client.get('/monitor/cameras/metrics', {
      params: { timezone },
    });
    return CameraMetricsSchema.parse(response.data);
  }

  async getCamera(serialNumber: string): Promise<CameraType> {
    const response = await this._client.get(`/monitor/cameras/${serialNumber}`);
    return CameraSchema.parse(response.data);
  }

  async createCamera(request: CreateCameraRequestType): Promise<CameraType> {
    const response = await this._client.post('/monitor/cameras', request);
    return CameraSchema.parse(response.data);
  }

  async updateCamera(
    serialNumber: string,
    request: UpdateCameraRequestType
  ): Promise<CameraType> {
    const response = await this._client.put(
      `/monitor/cameras/${serialNumber}`,
      request
    );
    return CameraSchema.parse(response.data);
  }

  // Maintenance Windows

  async getMaintenanceWindows(
    serialNumber: string
  ): Promise<MaintenanceWindowListType> {
    const response = await this._client.get(
      `/monitor/cameras/${serialNumber}/maintenance-windows`
    );
    return MaintenanceWindowListSchema.parse(response.data);
  }

  async createMaintenanceWindow(
    serialNumber: string,
    request: CreateMaintenanceWindowRequestType
  ): Promise<MaintenanceWindowType> {
    const response = await this._client.post(
      `/monitor/cameras/${serialNumber}/maintenance-windows`,
      request
    );
    return MaintenanceWindowSchema.parse(response.data);
  }

  async updateMaintenanceWindow(
    serialNumber: string,
    windowId: string,
    request: UpdateMaintenanceWindowRequestType
  ): Promise<MaintenanceWindowType> {
    const response = await this._client.put(
      `/monitor/cameras/${serialNumber}/maintenance-windows/${windowId}`,
      request
    );
    return MaintenanceWindowSchema.parse(response.data);
  }

  async deleteMaintenanceWindow(
    serialNumber: string,
    windowId: string
  ): Promise<void> {
    await this._client.delete(
      `/monitor/cameras/${serialNumber}/maintenance-windows/${windowId}`
    );
  }

  // Camera Alerts

  async getCameraAlerts(filters?: {
    /** When true, sends `locationId=all` (no location filter on the API). */
    allLocations?: boolean;
    /** One or more location ids; repeated as `locationId` query keys. */
    locationIds?: string[];
    cameraId?: string;
    severity?: string;
    active?: boolean;
    limit?: number;
  }): Promise<CameraAlertListType> {
    const { allLocations, locationIds, cameraId, severity, active, limit } =
      filters ?? {};

    const params: Record<string, unknown> = removeNullValuesFromObject({
      cameraId,
      severity,
      limit,
    });

    if (active === true || active === false) {
      params.active = active;
    }

    const multiLocationIds = locationIds && locationIds.length > 1;
    if (allLocations) {
      params.locationId = 'all';
    } else if (locationIds?.length === 1) {
      params.locationId = locationIds[0];
    } else if (multiLocationIds) {
      params.locationId = locationIds;
    }

    const response = await this._client.get('/monitor/cameras/alerts', {
      params,
      ...(multiLocationIds && {
        paramsSerializer: {
          indexes: null,
        },
      }),
    });
    return CameraAlertListSchema.parse(response.data);
  }

  async acknowledgeAlert(
    alertId: string,
    acknowledgedBy: string
  ): Promise<CameraAlertType> {
    const response = await this._client.post(
      `/monitor/cameras/alerts/${alertId}/acknowledge`,
      { acknowledgedBy }
    );
    return CameraAlertSchema.parse(response.data);
  }

  // Fleet Health

  async getFleetHealth(): Promise<FleetHealthType> {
    const response = await this._client.get('/monitor/cameras/fleet-health');
    return FleetHealthSchema.parse(response.data);
  }

  // Location Health

  async getLocationHealthConfig(locationId: string): Promise<HealthConfigType> {
    const response = await this._client.get(`/locations/${locationId}/health`);
    return HealthConfigSchema.parse(response.data);
  }

  async saveLocationHealthConfig(
    locationId: string,
    data: SaveHealthConfigPayloadType
  ): Promise<HealthConfigType> {
    const response = await this._client.put(
      `/locations/${locationId}/health`,
      data
    );
    return HealthConfigSchema.parse(response.data);
  }

  async getLocationHealthDiagnoses(
    locationId: string,
    params?: DiagnosesListParams
  ): Promise<PaginatedDiagnosesType> {
    const response = await this._client.get(
      `/locations/${locationId}/health/diagnoses`,
      { params: removeNullValuesFromObject(params ?? {}) }
    );
    return PaginatedDiagnosesSchema.parse(response.data);
  }

  async getLocationHealthDiagnosis(
    locationId: string,
    diagnosisId: string
  ): Promise<DiagnosisType> {
    const response = await this._client.get(
      `/locations/${locationId}/health/diagnoses/${diagnosisId}`
    );
    return DiagnosisSchema.parse(response.data);
  }

  async acknowledgeLocationHealthDiagnosis(
    locationId: string,
    diagnosisId: string
  ): Promise<void> {
    await this._client.post(
      `/locations/${locationId}/health/diagnoses/${diagnosisId}/acknowledge`
    );
  }

  async resolveLocationHealthDiagnosis(
    locationId: string,
    diagnosisId: string,
    notes: string
  ): Promise<void> {
    await this._client.post(
      `/locations/${locationId}/health/diagnoses/${diagnosisId}/resolve`,
      { notes }
    );
  }

  // PIN Management

  async getPINs(locationId: string): Promise<PINListType> {
    const response = await this._client.get(`/locations/${locationId}/pins`);
    return PINList.parse(response.data);
  }

  async createPIN(
    locationId: string,
    request: CreatePINRequestType
  ): Promise<PINType> {
    const response = await this._client.post(
      `/locations/${locationId}/pins`,
      request
    );
    return PIN.parse(response.data);
  }

  async deletePIN(locationId: string, pinId: string): Promise<void> {
    await this._client.delete(`/locations/${locationId}/pins/${pinId}`);
  }

  // Access Point Lockdown Overrides

  async getAccessPointOverride(
    accessPointId: string
  ): Promise<AccessPointLockdownOverrideType | null> {
    try {
      const response = await this._client.get(
        `/access-points/${accessPointId}/override`
      );
      return AccessPointLockdownOverride.parse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createAccessPointOverride(
    accessPointId: string,
    request: CreateLockdownOverrideRequestType
  ): Promise<AccessPointLockdownOverrideType> {
    const response = await this._client.post(
      `/access-points/${accessPointId}/override`,
      request
    );
    return AccessPointLockdownOverride.parse(response.data);
  }

  async clearAccessPointOverride(accessPointId: string): Promise<void> {
    await this._client.delete(`/access-points/${accessPointId}/override`);
  }

  async previewBroadcast(
    request: BroadcastPreviewRequest
  ): Promise<BroadcastPreviewResponse> {
    const response = await this._client.post('/broadcasts/preview', request);
    return BroadcastPreviewResponseSchema.parse(response.data);
  }

  async createBroadcast(
    request: BroadcastCompose,
    idempotencyKey: string
  ): Promise<BroadcastCampaign> {
    const response = await this._client.post('/broadcasts', request, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return BroadcastCampaignSchema.parse(response.data);
  }

  async getBroadcast(id: string): Promise<BroadcastCampaign> {
    const response = await this._client.get(`/broadcasts/${id}`);
    return BroadcastCampaignSchema.parse(response.data);
  }

  async listBroadcasts(locationId: string): Promise<BroadcastCampaign[]> {
    const response = await this._client.get('/broadcasts', {
      params: { locationId },
    });
    return BroadcastListResponseSchema.parse(response.data);
  }

  async postSupportRequest(payload: FormData): Promise<CreatedSupportRequest> {
    const response = await this._client.post('/support/requests', payload);
    return CreatedSupportRequestSchema.parse(response.data);
  }

  async getSupportRequests(): Promise<SupportRequest[]> {
    const response = await this._client.get('/support/requests');
    return z.array(SupportRequestSchema).parse(response.data);
  }

  async getSupportRequest(id: string): Promise<SupportRequestDetail> {
    const response = await this._client.get(`/support/requests/${id}`);
    return SupportRequestDetailSchema.parse(response.data);
  }

  async postSupportReply(
    id: string,
    payload: FormData
  ): Promise<SupportMessage> {
    const response = await this._client.post(
      `/support/requests/${id}/replies`,
      payload
    );
    return SupportMessageSchema.parse(response.data);
  }

  async postSupportFollowUp(
    id: string,
    body: { message?: string } = {}
  ): Promise<CreatedSupportRequest> {
    const response = await this._client.post(
      `/support/requests/${id}/follow-up`,
      body
    );
    return CreatedSupportRequestSchema.parse(response.data);
  }

  async patchSupportRequestRead(id: string): Promise<void> {
    await this._client.patch(`/support/requests/${id}/read`);
  }

  async listLocationAnnouncements(
    locationId: string
  ): Promise<LocationAnnouncementList> {
    const response = await this._client.get(
      `/location/${locationId}/announcements`
    );
    return LocationAnnouncementListSchema.parse(response.data);
  }

  async getLocationAnnouncement(
    locationId: string,
    announcementId: string
  ): Promise<LocationAnnouncement> {
    const response = await this._client.get(
      `/location/${locationId}/announcements/${announcementId}`
    );
    return LocationAnnouncementSchema.parse(response.data);
  }

  async createLocationAnnouncement(
    locationId: string,
    payload: CreateLocationAnnouncementPayload
  ): Promise<LocationAnnouncement> {
    const response = await this._client.post(
      `/location/${locationId}/announcements`,
      payload
    );
    return LocationAnnouncementSchema.parse(response.data);
  }

  async updateLocationAnnouncement(
    locationId: string,
    announcementId: string,
    payload: UpdateLocationAnnouncementPayload
  ): Promise<LocationAnnouncement> {
    const response = await this._client.put(
      `/location/${locationId}/announcements/${announcementId}`,
      payload
    );
    return LocationAnnouncementSchema.parse(response.data);
  }

  async deleteLocationAnnouncement(
    locationId: string,
    announcementId: string
  ): Promise<void> {
    await this._client.delete(
      `/location/${locationId}/announcements/${announcementId}`
    );
  }

  async listLocationLayouts(locationId: string): Promise<LocationLayoutList> {
    const response = await this._client.get(`/location/${locationId}/layouts`);
    return LocationLayoutListSchema.parse(response.data);
  }

  async updateLocationLayout(
    locationId: string,
    levelLabel: string,
    payload: UpdateLocationLayoutPayload
  ): Promise<LocationLayout> {
    const response = await this._client.put(
      `/location/${locationId}/layouts/${encodeURIComponent(levelLabel)}`,
      payload
    );
    return LocationLayoutSchema.parse(response.data);
  }

  async deleteLocationLayout(
    locationId: string,
    levelLabel: string
  ): Promise<void> {
    await this._client.delete(
      `/location/${locationId}/layouts/${encodeURIComponent(levelLabel)}`
    );
  }

  async uploadLocationLayoutImage(
    locationId: string,
    levelLabel: string,
    image: File
  ): Promise<LocationLayout> {
    const formData = new FormData();
    formData.append('image', image);
    // Don't set Content-Type - axios sets the multipart boundary automatically.
    const response = await this._client.post(
      `/location/${locationId}/layouts/${encodeURIComponent(levelLabel)}/image`,
      formData
    );
    return LocationLayoutSchema.parse(response.data);
  }

  async getAnalyticsCatalog(signal?: AbortSignal): Promise<AnalyticsCatalog> {
    const response = await this._client.get('/analytics/catalog', { signal });
    return AnalyticsCatalogSchema.parse(response.data);
  }

  // One entry per query, in request order. A widget that fails is answered in
  // its own result; the batch itself still answers 200. The server takes at
  // most ANALYTICS_MAX_BATCH queries per request, so a longer list is sent in
  // chunks and the results joined in order.
  async runAnalyticsQueries(
    queries: AnalyticsWidgetQuery[],
    signal?: AbortSignal
  ): Promise<AnalyticsQueryBatchResponse> {
    const chunks: AnalyticsWidgetQuery[][] = [];
    for (let i = 0; i < queries.length; i += ANALYTICS_MAX_BATCH) {
      chunks.push(queries.slice(i, i + ANALYTICS_MAX_BATCH));
    }
    const responses = await Promise.all(
      chunks.map(async (chunk) => {
        const response = await this._client.post(
          '/analytics/query',
          { queries: chunk },
          { signal }
        );
        return AnalyticsQueryBatchResponseSchema.parse(response.data);
      })
    );
    return { results: responses.flatMap((r) => r.results) };
  }
  async listBoards(signal?: AbortSignal): Promise<BoardList> {
    const response = await this._client.get('/analytics/boards', { signal });
    return BoardListSchema.parse(response.data);
  }

  async getDefaultBoard(signal?: AbortSignal): Promise<DefaultBoard> {
    const response = await this._client.get('/analytics/boards/default', {
      signal,
    });
    return DefaultBoardSchema.parse(response.data);
  }

  async getBoard(boardId: string, signal?: AbortSignal): Promise<Board> {
    const response = await this._client.get(
      `/analytics/boards/${encodeURIComponent(boardId)}`,
      { signal }
    );
    return BoardSchema.parse(response.data);
  }

  async createBoard(content: BoardContent, isDefault = false): Promise<Board> {
    const response = await this._client.post('/analytics/boards', {
      ...content,
      is_default: isDefault,
    });
    return BoardSchema.parse(response.data);
  }

  // The version read with the board is required: a stale one answers 409.
  async updateBoard(
    boardId: string,
    content: BoardContent,
    version: number
  ): Promise<Board> {
    const response = await this._client.put(
      `/analytics/boards/${encodeURIComponent(boardId)}`,
      { ...content, version }
    );
    return BoardSchema.parse(response.data);
  }

  async deleteBoard(boardId: string, version: number): Promise<void> {
    await this._client.delete(
      `/analytics/boards/${encodeURIComponent(boardId)}`,
      { params: { version } }
    );
  }

  async duplicateBoard(boardId: string, name?: string): Promise<Board> {
    const response = await this._client.post(
      `/analytics/boards/${encodeURIComponent(boardId)}/duplicate`,
      name ? { name } : {}
    );
    return BoardSchema.parse(response.data);
  }

  async setDefaultBoard(boardId: string, isDefault: boolean): Promise<Board> {
    const url = `/analytics/boards/${encodeURIComponent(boardId)}/default`;
    const response = isDefault
      ? await this._client.put(url)
      : await this._client.delete(url);
    return BoardSchema.parse(response.data);
  }
}
